import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { phoneProblem } from '../utils/phone';
import { emailProblem } from '../utils/email';
import { RemoteConsentSender } from '../components/volunteer/RemoteConsentSender';
import { BooleanObsGroups, toItems, groupItemsProblem } from '../components/volunteer/BooleanObsGroups';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import axios from 'axios';
import { ArrowLeft, X, FloppyDisk, Upload, File as FileIcon, Check, CalendarBlank, Printer, FileText, PencilSimple, Flask, WarningCircle, Plus, MagnifyingGlass, FingerprintSimple, EnvelopeSimple, Trash, ClipboardText, ClockCounterClockwise, Phone } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import FamilyPanel from '../components/volunteer/FamilyPanel';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import DentalChart from '../components/study/DentalChart';
import SendInfoSheetOnAssignDialog from '../components/study/SendInfoSheetOnAssignDialog';
import VolunteerAuditTimeline from '../components/volunteer/VolunteerAuditTimeline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOptions = [
  { value: 'activo', es: 'Activo', en: 'Active' },
  { value: 'inactivo', es: 'Inactivo', en: 'Inactive' },
];

// URS-F377: los 32 dientes FDI para el odontograma global de Q-Salud.
const FDI_ALL_TEETH = [
  "18","17","16","15","14","13","12","11","21","22","23","24","25","26","27","28",
  "48","47","46","45","44","43","42","41","31","32","33","34","35","36","37","38",
];

function validateDNI(value) {
  if (!value) return null;
  const v = value.toUpperCase().trim();
  const dniRegex = /^(\d{8})([A-Z])$/;
  const nieRegex = /^([XYZ])(\d{7})([A-Z])$/;
  const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
  let num, letter;
  if (dniRegex.test(v)) {
    const m = v.match(dniRegex);
    num = parseInt(m[1], 10);
    letter = m[2];
    if (letters[num % 23] !== letter) {
      return { valid: false, es: 'La letra del DNI no es correcta', en: 'DNI control letter is incorrect' };
    }
    return { valid: true };
  } else if (nieRegex.test(v)) {
    const m = v.match(nieRegex);
    const prefix = { X: '0', Y: '1', Z: '2' }[m[1]];
    num = parseInt(prefix + m[2], 10);
    letter = m[3];
    if (letters[num % 23] !== letter) {
      return { valid: false, es: 'La letra del NIE no es correcta', en: 'NIE control letter is incorrect' };
    }
    return { valid: true };
  }
  return { valid: true };
}


function ComputedAge({ birthDate, lang }) {
  if (!birthDate) return (
    <div className="mb-5 flex items-center gap-3 px-4 py-3 bg-stone-50 border border-stone-200 rounded-sm">
      <Label className="text-sm font-medium text-stone-500">{lang === 'es' ? 'Edad' : 'Age'}:</Label>
      <span className="text-sm text-stone-400 italic" data-testid="computed-age">{lang === 'es' ? 'Sin fecha de nacimiento' : 'No birth date'}</span>
    </div>
  );
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return (
    <div className="mb-5 flex items-center gap-3 px-4 py-3 bg-stone-50 border border-stone-200 rounded-sm">
      <Label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Edad' : 'Age'}:</Label>
      <span className="text-lg font-semibold text-[#1f3e80]" data-testid="computed-age">{age} {lang === 'es' ? 'años' : 'years'}</span>
    </div>
  );
}

// URS-F387: version inline (misma logica) para colocar junto al campo
// "Fecha de nacimiento" en Datos Personales, ocupando la mitad de la
// columna que antes tenia el date input.
function InlineAge({ birthDate, lang }) {
  let ageText = lang === 'es' ? '—' : '—';
  if (birthDate) {
    const today = new Date();
    const birth = new Date(birthDate);
    if (!Number.isNaN(birth.getTime())) {
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      ageText = `${age} ${lang === 'es' ? 'años' : 'years'}`;
    }
  }
  return (
    <div className="space-y-1.5">
      {/* Mismo wrapper que DynamicField (flex + justify-between) para que la
          altura del Label sea identica y las cajas queden alineadas al pixel. */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-stone-800 font-body">{lang === 'es' ? 'Edad' : 'Age'}</Label>
      </div>
      <Input
        data-testid="field-edad-computed"
        value={ageText}
        readOnly
        tabIndex={-1}
        className="bg-stone-50 font-semibold text-[#1f3e80] cursor-default"
      />
    </div>
  );
}

function DynamicField({ field, value, onChange, lang, readOnly, isMinor, onMinorChange }) {
  const label = lang === 'es' ? field.label_es : field.label_en;
  const val = value || '';
  const fullWidth = !!field.full_width;
  const fwCls = fullWidth ? ' md:col-span-2' : '';

  if (field.type === 'boolean_obs') {
    // value es objeto {value:'si'|'no', obs:'...'}. Retrocompat: string plano => {value:'si', obs:string}
    const raw = value;
    const cur = (raw && typeof raw === 'object') ? raw : (raw ? { value: 'si', obs: String(raw) } : { value: '', obs: '' });
    const obsLabel = (lang === 'es' ? field.obs_label_es : field.obs_label_en) || (lang === 'es' ? 'Especificar' : 'Specify');
    const obsReq = field.obs_required !== false;
    const setVal = field.group_selector
      ? (v) => onChange(v === 'no' ? { value: 'no', obs: '', items: [] } : { value: v, obs: '', items: toItems(cur) })
      : (v) => onChange(v === 'no' ? { value: 'no', obs: '' } : { value: v, obs: cur.obs || '' });
    const setObs = (t) => onChange({ value: cur.value || 'si', obs: t });
    const expanded = cur.value === 'si';
    // Al responder "Sí" el campo ocupa toda la fila (2 col): selector a la
    // izquierda y observaciones a la derecha. Cualquier pregunta que estuviese
    // al lado se recoloca en la fila de abajo automaticamente.
    const wrapCls = expanded ? ' md:col-span-2' : fwCls;
    return (
      <div className={`${expanded ? 'grid grid-cols-1 md:grid-cols-2 gap-5 items-start' : 'space-y-1.5'}${wrapCls}`}>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-stone-800 font-body">
            {label}{field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Select value={cur.value} onValueChange={setVal} disabled={readOnly}>
            <SelectTrigger data-testid={`field-${field.field_key}`} className="bg-white border-stone-200">
              <SelectValue placeholder={lang === 'es' ? 'Seleccionar...' : 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="si">{lang === 'es' ? 'Sí' : 'Yes'}</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {expanded && field.group_selector && (
          <BooleanObsGroups field={field} cur={cur} lang={lang} readOnly={readOnly} onChange={onChange} />
        )}
        {expanded && !field.group_selector && (
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-stone-800 font-body">
              {obsLabel}{obsReq && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              data-testid={`field-${field.field_key}-obs`}
              value={cur.obs || ''}
              onChange={e => setObs(e.target.value)}
              readOnly={readOnly}
              className={`bg-white border-stone-200 min-h-[70px] ${readOnly ? 'bg-stone-50 text-stone-500 cursor-not-allowed' : ''} ${obsReq && !(cur.obs || '').trim() ? 'border-amber-400' : ''}`}
            />
          </div>
        )}
      </div>
    );
  }
  if (field.type === 'select') {
    return (
      <div className={`space-y-1.5${fwCls}`}>
        <Label className="text-sm font-medium text-stone-800 font-body">
          {label}{field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <Select value={val} onValueChange={onChange} disabled={readOnly}>
          <SelectTrigger data-testid={`field-${field.field_key}`} className="bg-white border-stone-200">
            <SelectValue placeholder={lang === 'es' ? 'Seleccionar...' : 'Select...'} />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === 'multiselect') {
    const selected = Array.isArray(value) ? value : (value ? value.split(',').map(s => s.trim()).filter(Boolean) : []);
    const toggleOption = (opt) => {
      if (readOnly) return;
      const next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
      onChange(next);
    };
    return (
      <div className="space-y-1.5 md:col-span-2">
        <Label className="text-sm font-medium text-stone-800 font-body">
          {label}{field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <div data-testid={`field-${field.field_key}`} className={`flex flex-wrap gap-2 p-3 border border-stone-200 rounded-md bg-white ${readOnly ? 'bg-stone-50 opacity-70' : ''}`}>
          {(field.options || []).map(opt => (
            <button key={opt} type="button" onClick={() => toggleOption(opt)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selected.includes(opt) ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'} ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              data-testid={`multiselect-${field.field_key}-${opt}`}>
              {opt}
            </button>
          ))}
        </div>
        {selected.length > 0 && (
          <p className="text-xs text-stone-400">{selected.length} {lang === 'es' ? 'seleccionados' : 'selected'}</p>
        )}
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div className="space-y-1.5 md:col-span-2">
        <Label className="text-sm font-medium text-stone-800 font-body">
          {label}{field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <Textarea data-testid={`field-${field.field_key}`} value={val} onChange={e => onChange(e.target.value)} readOnly={readOnly} className={`bg-white border-stone-200 min-h-[80px] ${readOnly ? 'bg-stone-50 text-stone-500 cursor-not-allowed' : ''}`} />
      </div>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <div className={`flex items-center gap-3 pt-6${fwCls}`}>
        <Switch data-testid={`field-${field.field_key}`} checked={!!val} onCheckedChange={onChange} disabled={readOnly} />
        <Label className="text-sm font-medium text-stone-800 font-body">{label}</Label>
      </div>
    );
  }
  const isDNI = field.field_key === 'dni';
  const dniResult = isDNI && val ? validateDNI(val) : null;
  const isRequired = field.required && !(isDNI && isMinor);

  return (
    <div className={`space-y-1.5${fwCls}`}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-stone-800 font-body">
          {label}{isRequired && <span className="text-red-500 ml-1">*</span>}
        </Label>
        {isDNI && onMinorChange && (
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="minor-toggle">
            <Switch checked={!!isMinor} onCheckedChange={onMinorChange} className="scale-90" />
            <span className="text-xs text-stone-500">{lang === 'es' ? 'Menor sin DNI' : 'Minor without ID'}</span>
          </label>
        )}
      </div>
      <div className="relative">
        <Input
          data-testid={`field-${field.field_key}`}
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          value={val}
          onChange={e => onChange(isDNI ? e.target.value.toUpperCase() : e.target.value)}
          readOnly={readOnly}
          className={`bg-white border-stone-200 ${readOnly ? 'bg-stone-50 text-stone-500 cursor-not-allowed' : ''} ${isDNI && dniResult && !dniResult.valid ? 'border-red-400 focus:border-red-400 focus:ring-red-400' : ''} ${isDNI && dniResult?.valid ? 'border-green-400 focus:border-green-400 focus:ring-green-400' : ''}`}
          placeholder={isDNI ? (isMinor ? (lang === 'es' ? 'Opcional para menores' : 'Optional for minors') : 'DNI / NIE / Pasaporte') : undefined}
          maxLength={isDNI ? 20 : undefined}
        />
        {isDNI && dniResult?.valid && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xs font-mono">OK</span>}
      </div>
      {isDNI && dniResult && !dniResult.valid && (
        <p className="text-xs text-red-500 font-body" data-testid="dni-error">{lang === 'es' ? dniResult.es : dniResult.en}</p>
      )}
      {field.field_key === 'email' && emailProblem(val, lang) && (
        <p className="text-xs text-red-500 font-body" data-testid="email-error">{emailProblem(val, lang)}</p>
      )}
      {field.field_key === 'telefono' && phoneProblem(val, lang) && (
        <p className="text-xs text-red-500 font-body" data-testid="phone-error">{phoneProblem(val, lang)}</p>
      )}
    </div>
  );
}


function ConsentDocSection({ template, docData, volunteerId, onSigned, onUpload, onDelete, isAdmin, uploading, lang }) {
  const [signing, setSigning] = useState(false);
  // URS-F308: dialogo de confirmacion de borrado (solo admin)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/volunteers/${volunteerId}/documents/${template.type}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Documento eliminado' : 'Document deleted');
      setShowDeleteDialog(false);
      // Notify parent so it clears `documents[template.type]` from local state.
      if (onDelete) onDelete(template.type);
    } catch (err) {
      const msg = err?.response?.data?.detail || (lang === 'es' ? 'Error al eliminar el documento' : 'Failed to delete document');
      toast.error(typeof msg === 'string' ? msg : 'Error');
    } finally {
      setDeleting(false);
    }
  };

  const openFile = () => {
    if (docData?.file_id) {
      window.open(`${API_URL}/api/files/download/${docData.file_id}`, '_blank');
    }
  };

  const handlePreview = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/consent-templates/${template.template_id}/generate/${volunteerId}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { toast.error('Error al previsualizar'); }
  };

  const handleSign = async () => {
    setSigning(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/consent-templates/${template.template_id}/sign/${volunteerId}`, {}, { withCredentials: true });
      toast.success(lang === 'es' ? 'Consentimiento firmado y archivado' : 'Consent signed and archived');
      onSigned(data.doc_key, data.document);
    } catch (err) { toast.error(errText(err)); }
    setSigning(false);
  };

  const [sendingVid, setSendingVid] = useState(false);
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [loadingDevices, setLoadingDevices] = useState(false);

  const openDevicePicker = async () => {
    setDevicePickerOpen(true);
    setLoadingDevices(true);
    try {
      const r = await axios.get(`${API_URL}/api/vidsigner/devices`, { withCredentials: true });
      const items = r.data?.items || [];
      setDevices(items);
      if (items.length === 1) setSelectedDevice(items[0].device_name);
      else if (items.length === 0) {
        toast.warning(lang === 'es'
          ? 'No hay tabletas sincronizadas. Sincroniza desde Configuracion -> Integraciones.'
          : 'No tablets synced. Sync from Settings -> Integrations.');
      }
    } catch (err) {
      toast.error(errText(err, 'Error cargando tabletas'));
    }
    setLoadingDevices(false);
  };

  const handleSendToVidsigner = async () => {
    if (!selectedDevice) {
      toast.error(lang === 'es' ? 'Selecciona una tableta' : 'Select a tablet');
      return;
    }
    setSendingVid(true);
    try {
      // 1) Generate the consent PDF as a blob
      const resp = await axios.get(`${API_URL}/api/consent-templates/${template.template_id}/generate/${volunteerId}`, { withCredentials: true, responseType: 'blob' });
      // 2) Convert to base64
      const blob = resp.data;
      const buffer = await blob.arrayBuffer();
      let bin = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
      const b64 = window.btoa(bin);
      // 3) Send to VIDsigner backend (clauses are answered on the tablet via
      // VIDsigner Form; backend post-stamps the answers on the signed PDF).
      const r = await axios.post(`${API_URL}/api/vidsigner/send`, {
        pdf_base64: b64,
        document_name: template.name,
        volunteer_id: volunteerId,
        doc_kind: 'consent',
        template_id: template.template_id,
        template_type: template.type,
        device_name: selectedDevice,
      }, { withCredentials: true });
      toast.success(lang === 'es'
        ? `Enviado a firmar (DocGUI: ${r.data.doc_gui.slice(0, 8)}...). Se archivara automaticamente al firmarse.`
        : `Sent to VIDsigner (DocGUI: ${r.data.doc_gui.slice(0, 8)}...). It will be archived automatically once signed.`);
      onSigned && onSigned();
      setDevicePickerOpen(false);
    } catch (err) { toast.error(errText(err, 'Error VIDsigner')); }
    setSendingVid(false);
  };

  const syncDevices = async () => {
    setLoadingDevices(true);
    try {
      const r = await axios.get(`${API_URL}/api/vidsigner/devices?sync=true`, { withCredentials: true });
      const items = r.data?.items || [];
      setDevices(items);
      toast.success(lang === 'es' ? `${items.length} tabletas sincronizadas` : `${items.length} tablets synced`);
    } catch (err) { toast.error(errText(err, 'Error sync')); }
    setLoadingDevices(false);
  };

  const isSigned = docData?.signed;

  return (
    <div className={`bg-white border rounded-sm p-6 ${isSigned ? 'border-green-300 bg-green-50/30' : 'border-stone-200'}`} data-testid={`consent-doc-${template.type}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText size={20} className={isSigned ? 'text-green-600' : 'text-stone-400'} />
          <h3 className="text-sm font-heading font-semibold text-stone-900">{template.name}</h3>
          {isSigned && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-300">{lang === 'es' ? 'Firmado' : 'Signed'}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {isSigned && <Check size={22} className="text-green-600" weight="bold" />}
          {/* URS-F308: borrar archivo (admin only) */}
          {isAdmin && docData?.file_id && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
              data-testid={`consent-delete-${template.type}`}
              onClick={() => setShowDeleteDialog(true)}
              title={lang === 'es' ? 'Eliminar archivo (admin)' : 'Delete file (admin)'}
            >
              <Trash size={16} />
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-stone-500 mb-4 ml-7">{template.title}</p>

      {isSigned ? (
        <div className="ml-7 space-y-2">
          <div className="flex items-center gap-3">
            <FileIcon size={18} className="text-emerald-700" />
            <button type="button" onClick={openFile} className="text-sm font-medium text-emerald-700 hover:underline cursor-pointer" data-testid={`consent-open-${template.type}`}>
              {docData.file_name}
            </button>
            {docData.signature_method === 'vidsigner' && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-sm">
                <FingerprintSimple size={10} weight="bold" /> VIDsigner
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 text-[11px] text-stone-400">
            {docData.signed_by && <span>{lang === 'es' ? 'Firmado por' : 'Signed by'}: {docData.signed_by}</span>}
            {docData.signed_at && <span>{new Date(docData.signed_at).toLocaleString()}</span>}
            {!docData.signed_by && docData.uploaded_at && <span>{lang === 'es' ? 'Subido' : 'Uploaded'}: {new Date(docData.uploaded_at).toLocaleString()}</span>}
          </div>
          {docData.clause_answers && Object.keys(docData.clause_answers).length > 0 && (
            <div className="text-[11px] text-stone-500 space-y-0.5 mt-1">
              {Object.entries(docData.clause_answers).map(([cid, ans]) => (
                <div key={cid} className="flex items-start gap-1">
                  <span className={ans.response ? 'text-emerald-600' : 'text-stone-400'}>{ans.response ? '✓' : '✗'}</span>
                  <span className="truncate max-w-md">{ans.title || cid}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1">
            <Button variant="outline" size="sm" className="text-xs" onClick={openFile}
              data-testid={`consent-view-signed-${template.type}`}>
              <FileText size={12} className="mr-1" /> {lang === 'es' ? 'Ver PDF firmado' : 'View signed PDF'}
            </Button>
            <Button size="sm" variant="outline" onClick={openDevicePicker} disabled={sendingVid}
              className="text-xs border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-800 gap-1"
              data-testid={`consent-resend-vidsigner-${template.type}`}
              title={lang === 'es' ? 'Regenerar el PDF y enviarlo de nuevo a la tableta' : 'Regenerate the PDF and resend to the tablet'}
            >
              <FingerprintSimple size={12} />
              {sendingVid
                ? (lang === 'es' ? 'Enviando...' : 'Sending...')
                : (lang === 'es' ? 'Reenviar a tableta' : 'Resend to tablet')}
            </Button>
            {docData.signature_method !== 'vidsigner' && (
              <label className="cursor-pointer">
                <Button variant="ghost" size="sm" className="text-xs pointer-events-none" asChild>
                  <span><Upload size={12} className="mr-1" /> {lang === 'es' ? 'Reemplazar archivo' : 'Replace file'}</span>
                </Button>
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={e => e.target.files[0] && onUpload(template.type, e.target.files[0])} />
              </label>
            )}
          </div>
        </div>
      ) : null}

      {/* Device picker dialog (VIDsigner) */}
      <Dialog open={devicePickerOpen} onOpenChange={setDevicePickerOpen}>
        <DialogContent className="max-w-md" data-testid={`vidsigner-device-dialog-${template.type}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-800">
              <FingerprintSimple size={20} weight="bold" />
              {lang === 'es' ? 'Selecciona la tableta' : 'Select the tablet'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-stone-500">
              {lang === 'es'
                ? 'Selecciona la tableta. El voluntario respondera las clausulas y firmara directamente en la tableta.'
                : 'Pick a tablet. The volunteer will answer clauses and sign directly on the tablet.'}
            </p>

            {loadingDevices ? (
              <p className="text-sm text-stone-400 text-center py-4">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
            ) : devices.length === 0 ? (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3">
                {lang === 'es'
                  ? 'No hay tabletas sincronizadas. Pulsa "Sincronizar" para obtener la lista de Validated ID.'
                  : 'No tablets synced. Press "Sync" to fetch the list from Validated ID.'}
              </div>
            ) : (
              <div className="space-y-1">
                <Label className="text-xs">{lang === 'es' ? 'Tableta' : 'Tablet'}</Label>
                <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                  <SelectTrigger data-testid={`vidsigner-device-select-${template.type}`}>
                    <SelectValue placeholder={lang === 'es' ? 'Selecciona una tableta...' : 'Select a tablet...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {devices.map(d => (
                      <SelectItem key={d.device_name} value={d.device_name}>
                        {d.device_description || d.device_name}
                        <span className="text-[10px] text-stone-400 ml-2">{d.device_name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-between items-center pt-2">
              <Button variant="ghost" size="sm" onClick={syncDevices} disabled={loadingDevices} data-testid={`vidsigner-sync-devices-${template.type}`}>
                {lang === 'es' ? 'Sincronizar' : 'Sync'}
              </Button>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDevicePickerOpen(false)} className="rounded-sm">
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleSendToVidsigner} disabled={!selectedDevice || sendingVid}
              className="bg-violet-700 hover:bg-violet-800 text-white rounded-sm"
              data-testid={`vidsigner-confirm-send-${template.type}`}>
              {sendingVid ? (lang === 'es' ? 'Enviando...' : 'Sending...') : (lang === 'es' ? 'Enviar a firmar' : 'Send to sign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!isSigned && (
        <div className="ml-7 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handlePreview} data-testid={`consent-preview-${template.type}`}>
              <FileText size={14} className="mr-1" /> {lang === 'es' ? 'Ver documento' : 'View document'}
            </Button>
            <Button size="sm" onClick={openDevicePicker} disabled={sendingVid}
              className="bg-violet-700 hover:bg-violet-800 text-white gap-1"
              data-testid={`consent-vidsigner-${template.type}`}
              title={lang === 'es' ? 'Enviar a la tableta para firma biometrica' : 'Send to tablet for biometric signature'}
            >
              <FingerprintSimple size={14} />
              {sendingVid
                ? (lang === 'es' ? 'Enviando...' : 'Sending...')
                : (lang === 'es' ? 'Enviar a firmar (VIDsigner)' : 'Send to VIDsigner')
              }
            </Button>
          </div>
          <div className="border-2 border-dashed border-stone-200 rounded-sm p-4 text-center">
            <label className="cursor-pointer block">
              <Upload size={24} className="text-stone-300 mx-auto mb-1" />
              <p className="text-xs text-stone-500">
                {uploading
                  ? (lang === 'es' ? 'Subiendo...' : 'Uploading...')
                  : (lang === 'es' ? 'O sube el documento firmado en otro sitio' : 'Or upload the document signed elsewhere')
                }
              </p>
              <p className="text-[10px] text-stone-400 mt-0.5">PDF, JPG, PNG</p>
              <input type="file" className="hidden" data-testid={`consent-upload-${template.type}`} accept=".pdf,.jpg,.jpeg,.png" onChange={e => e.target.files[0] && onUpload(template.type, e.target.files[0])} />
            </label>
          </div>
        </div>
      )}

      {/* URS-F308: confirmacion de borrado del documento */}
      <ConfirmDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
        confirming={deleting}
        title={lang === 'es' ? `Eliminar "${template.name}"` : `Delete "${template.name}"`}
        description={lang === 'es'
          ? `Esta acción eliminará el archivo (${docData?.file_name || 'documento'}) del voluntario. Esta acción queda registrada en auditoría y NO se puede deshacer.`
          : `This will delete the file (${docData?.file_name || 'document'}) from the volunteer. The action is logged in the audit trail and CANNOT be undone.`}
        confirmLabel={lang === 'es' ? 'Eliminar' : 'Delete'}
        cancelLabel={lang === 'es' ? 'Cancelar' : 'Cancel'}
      />
    </div>
  );
}


const apptStatusColors = {
  citada: 'bg-blue-50 text-blue-700 border-blue-200',
  anulada: 'bg-stone-50 text-stone-500 border-stone-200',
  no_asiste: 'bg-red-50 text-red-600 border-red-200',
  sala_espera: 'bg-amber-50 text-amber-700 border-amber-200',
  especialista: 'bg-purple-50 text-purple-700 border-purple-200',
  sondas: 'bg-teal-50 text-teal-700 border-teal-200',
  terminada: 'bg-green-50 text-green-700 border-green-200',
};

// URS-F397: KPIs de fiabilidad del voluntario a partir del historico de citas.
//   asistidas = todo lo que NO sea futura (citada), NO sea anulada y NO sea no_asiste
//   falladas  = anulada + no_asiste
//   ratio     = asistidas / (asistidas + falladas) * 100
function AttendanceRatioPanel({ appointments, studies, lang }) {
  const stats = (() => {
    let asistidas = 0;
    let anuladas = 0;
    let noAsiste = 0;
    let citadas = 0;
    for (const a of appointments) {
      if (a.status === 'anulada') anuladas++;
      else if (a.status === 'no_asiste') noAsiste++;
      else if (a.status === 'citada') citadas++;
      else asistidas++;
    }
    const falladas = anuladas + noAsiste;
    const base = asistidas + falladas;
    const ratio = base === 0 ? null : Math.round((asistidas / base) * 100);
    return { asistidas, anuladas, noAsiste, citadas, falladas, base, ratio };
  })();

  const ratioColor = stats.ratio === null
    ? 'bg-stone-100 text-stone-500 border-stone-200'
    : stats.ratio >= 80
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : stats.ratio >= 50
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-red-50 text-red-700 border-red-200';
  const ratioLabel = stats.ratio === null
    ? (lang === 'es' ? 'Sin datos suficientes' : 'Not enough data')
    : `${stats.ratio}%`;

  const byStudy = {};
  for (const a of appointments) {
    if (a.status === 'citada') continue;
    const key = a.study_id || '__qsalud__';
    if (!byStudy[key]) byStudy[key] = { asistidas: 0, anuladas: 0, noAsiste: 0 };
    if (a.status === 'anulada') byStudy[key].anuladas++;
    else if (a.status === 'no_asiste') byStudy[key].noAsiste++;
    else byStudy[key].asistidas++;
  }
  const perStudyRows = Object.entries(byStudy)
    .map(([sid, s]) => {
      const total = s.asistidas + s.anuladas + s.noAsiste;
      const pct = total === 0 ? null : Math.round((s.asistidas / total) * 100);
      const study = studies[sid];
      return {
        study_id: sid,
        code: sid === '__qsalud__'
          ? (lang === 'es' ? 'Q Salud / sin estudio' : 'Q Salud / no study')
          : (study?.title || study?.code || sid),
        ...s,
        total,
        pct,
      };
    })
    .sort((a, b) => (a.pct ?? 200) - (b.pct ?? 200));

  return (
    <div className="space-y-4" data-testid="attendance-ratio-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={`border rounded-sm p-4 flex flex-col ${ratioColor}`} data-testid="attendance-ratio-kpi">
          <span className="text-xs opacity-70">{lang === 'es' ? 'Ratio de asistencia' : 'Attendance ratio'}</span>
          <span className="text-3xl font-heading font-semibold mt-1">{ratioLabel}</span>
          {stats.base > 0 && (
            <span className="text-[11px] opacity-70 mt-1">
              {stats.asistidas} / {stats.base} {lang === 'es' ? 'citas' : 'appts'}
            </span>
          )}
        </div>
        <div className="border rounded-sm p-4 bg-emerald-50 text-emerald-700 border-emerald-200 flex flex-col">
          <span className="text-xs opacity-70">{lang === 'es' ? 'Asistidas' : 'Attended'}</span>
          <span className="text-3xl font-heading font-semibold mt-1" data-testid="attendance-attended-count">{stats.asistidas}</span>
        </div>
        <div className="border rounded-sm p-4 bg-red-50 text-red-700 border-red-200 flex flex-col">
          <span className="text-xs opacity-70">{lang === 'es' ? 'No asisti\u00f3' : 'No show'}</span>
          <span className="text-3xl font-heading font-semibold mt-1" data-testid="attendance-noshow-count">{stats.noAsiste}</span>
        </div>
        <div className="border rounded-sm p-4 bg-stone-50 text-stone-600 border-stone-200 flex flex-col">
          <span className="text-xs opacity-70">{lang === 'es' ? 'Anuladas' : 'Cancelled'}</span>
          <span className="text-3xl font-heading font-semibold mt-1" data-testid="attendance-cancelled-count">{stats.anuladas}</span>
        </div>
      </div>

      {stats.citadas > 0 && (
        <p className="text-xs text-stone-500 italic">
          {lang === 'es'
            ? `Nota: ${stats.citadas} cita(s) futuras (Citada) no cuentan en el ratio.`
            : `Note: ${stats.citadas} future appointment(s) (Scheduled) are not counted in the ratio.`}
        </p>
      )}

      {perStudyRows.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-stone-800 mb-2">
            {lang === 'es' ? 'Desglose por estudio' : 'Breakdown by study'}
          </h4>
          <div className="border border-stone-200 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{lang === 'es' ? 'Estudio' : 'Study'}</TableHead>
                  <TableHead className="text-center w-24">{lang === 'es' ? 'Asistidas' : 'Attended'}</TableHead>
                  <TableHead className="text-center w-24">{lang === 'es' ? 'No asisti\u00f3' : 'No show'}</TableHead>
                  <TableHead className="text-center w-24">{lang === 'es' ? 'Anuladas' : 'Cancelled'}</TableHead>
                  <TableHead className="text-center w-24">{lang === 'es' ? 'Ratio' : 'Ratio'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perStudyRows.map(r => {
                  const cls = r.pct === null
                    ? 'bg-stone-50 text-stone-500 border-stone-200'
                    : r.pct >= 80
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : r.pct >= 50
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-red-50 text-red-700 border-red-200';
                  return (
                    <TableRow key={r.study_id} data-testid={`attendance-study-row-${r.study_id}`}>
                      <TableCell className="text-sm font-medium">{r.code}</TableCell>
                      <TableCell className="text-sm text-center">{r.asistidas}</TableCell>
                      <TableCell className="text-sm text-center">{r.noAsiste}</TableCell>
                      <TableCell className="text-sm text-center">{r.anuladas}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-xs ${cls}`}>
                          {r.pct === null ? '\u2014' : `${r.pct}%`}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

function AppointmentsPanel({ volunteerId, volunteerName, lang }) {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [studies, setStudies] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCancelled, setShowCancelled] = useState(false);
  const [savingStatus, setSavingStatus] = useState({}); // {appointment_id: true}
  const [apptToDelete, setApptToDelete] = useState(null);
  const [deletingAppt, setDeletingAppt] = useState(false);
  // URS-F397: sub-pestana dentro de "Citas" (list | ratio)
  const [subTab, setSubTab] = useState('list');

  const lb = lang === 'es'
    ? { title: 'Citas del voluntario', no_appts: 'Este voluntario no tiene citas programadas', date: 'Fecha', time: 'Hora', study: 'Estudio', visit: 'Visita', status: 'Estado', citada: 'Citada', anulada: 'Anulada', no_asiste: 'No asiste', sala_espera: 'Sala de espera', especialista: 'Especialista', sondas: 'Sondas', terminada: 'Terminada', print: 'Imprimir citas', email: 'Enviar por correo', all: 'Todas', filter_by: 'Filtrar por estado', justificante: 'Justificante de asistencia', justificante_row: 'Imprimir', no_terminada: 'Solo disponible en citas terminadas' }
    : { title: 'Volunteer appointments', no_appts: 'This volunteer has no scheduled appointments', date: 'Date', time: 'Time', study: 'Study', visit: 'Visit', status: 'Status', citada: 'Scheduled', anulada: 'Cancelled', no_asiste: 'No show', sala_espera: 'Waiting room', especialista: 'Specialist', sondas: 'Probes', terminada: 'Completed', print: 'Print appointments', email: 'Send by email', all: 'All', filter_by: 'Filter by status', justificante: 'Attendance certificate', justificante_row: 'Print', no_terminada: 'Only available for completed appointments' };

  const statusOptions = [
    { value: 'all', label: lb.all },
    { value: 'citada', label: lb.citada },
    { value: 'sala_espera', label: lb.sala_espera },
    { value: 'especialista', label: lb.especialista },
    { value: 'sondas', label: lb.sondas },
    { value: 'terminada', label: lb.terminada },
    { value: 'no_asiste', label: lb.no_asiste },
    { value: 'anulada', label: lb.anulada },
  ];

  useEffect(() => {
    if (!volunteerId) { setLoading(false); return; }
    Promise.all([
      axios.get(`${API_URL}/api/appointments?volunteer_id=${volunteerId}&include_cancelled=true`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies`, { withCredentials: true }),
    ]).then(([ar, sr]) => {
      const studyMap = {};
      sr.data.forEach(s => { studyMap[s.study_id] = s; });
      setStudies(studyMap);
      const sorted = (ar.data || []).sort((a, b) => {
        const da = `${a.date}T${a.time || '00:00'}`;
        const db2 = `${b.date}T${b.time || '00:00'}`;
        return da.localeCompare(db2);
      });
      setAppointments(sorted);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [volunteerId]);

  const handlePrint = () => {
    const scheduled = appointments.filter(a => a.status === 'citada');
    if (scheduled.length === 0) return;
    const logoUrl = window.location.origin + '/logo-goya.png';
    // URS-F250: ticket impresion - fuentes ampliadas significativamente para que
    // el voluntario pueda leer la informacion sin esfuerzo. Mantenemos el ancho
    // de 80mm (impresora termica) pero subimos todos los tamanos ~50-80% y
    // anadimos la columna "Visita" con el codigo (VAL, T0, T14D...) y el
    // subtipo del estudio, mas espacio entre filas.
    // URS-F491 (privacidad/ciego): NO imprimimos el nombre del voluntario
    // (sustituido por su codigo). El SUBTIPO del estudio (phase) SI se
    // imprime; si el estudio no tiene subtipo (ej. Medicamento) queda en
    // blanco (NUNCA hacemos fallback al codigo del estudio).
    const rows = scheduled.map(a => {
      const visitInfo = a.visit_code || '';
      const studyInfo = studies[a.study_id]?.phase || '';
      return `<tr>
        <td class="c-date">${a.date}</td>
        <td class="c-time">${a.time || '-'}</td>
        <td class="c-visit">${visitInfo}<span class="study-sub">${studyInfo}</span></td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><title>Citas</title><style>
      @page { size: 80mm auto; margin: 0 1mm 1mm 1mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; width: 76mm; padding: 0 1mm 1.5mm 1mm; color: #000; font-size: 12px; line-height: 1.3; }
      .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 1.5mm; margin-bottom: 1.5mm; }
      .header img { width: 55mm; height: auto; margin-bottom: 0.5mm; filter: grayscale(100%) contrast(160%); }
      .header .phone { font-size: 11px; margin-top: 0.8mm; font-weight: 600; }
      .header .ev-clinicas { font-size: 10px; margin-top: 0.6mm; letter-spacing: 0.3px; }
      .header .name { font-size: 12px; font-weight: 700; margin-top: 1.5mm; text-transform: uppercase; line-height: 1.15; }
      .header .subtitle { font-size: 11px; margin-top: 1mm; font-weight: 600; letter-spacing: 1px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 1.5mm; }
      th { font-size: 10px; text-align: left; border-bottom: 2px solid #000; padding: 1mm 1mm; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
      td { font-size: 13px; padding: 2mm 1mm; border-bottom: 1px dashed #999; font-weight: 600; vertical-align: top; }
      td.c-date { white-space: nowrap; }
      td.c-time { font-weight: 700; }
      td.c-visit { font-weight: 700; }
      td.c-visit .study-sub { display: block; font-size: 9px; font-weight: 500; color: #000; margin-top: 0.4mm; text-transform: uppercase; letter-spacing: 0.3px; }
    </style></head><body>
      <div class="header">
        <img src="${logoUrl}" alt="Goya Innova" />
        <div class="phone">Tel: 911 928 904</div>
        <div class="ev-clinicas">Evaluaciones Cl\u00ednicas</div>
        <div class="name">${volunteerId || ''}</div>
        <div class="subtitle">${lang === 'es' ? 'CITAS' : 'APPOINTMENTS'}</div>
      </div>
      <table><thead><tr><th>${lb.date}</th><th>${lb.time}</th><th>${lang === 'es' ? 'Visita' : 'Visit'}</th></tr></thead><tbody>${rows}</tbody></table>
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`;
    const w = window.open('', '_blank');
    w.document.open();
    w.document.writeln(html);
    w.document.close();
  };

  const scheduledCount = appointments.filter(a => a.status === 'citada').length;
  // URS-F346: incluir tambien las citas Qsalud (sin study_id) en el contador
  // de envio por email. El endpoint ahora acepta study_id=null para Qsalud.
  const emailableCount = appointments.filter(a => a.status === 'citada' && a.visit_code !== 'QSALUD TFNO').length;
  const [sendingEmail, setSendingEmail] = useState(false);
  // URS-F390: descarga del "Justificante de asistencia" en PDF para una cita.
  const [showJustDialog, setShowJustDialog] = useState(false);
  const [downloadingJustId, setDownloadingJustId] = useState(null);
  const downloadJustificante = async (appointmentId, dateHint) => {
    try {
      setDownloadingJustId(appointmentId);
      const resp = await axios.get(
        `${API_URL}/api/appointments/${appointmentId}/attendance-certificate.pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      // URS-F390 fix: abrir directamente el dialogo de impresion del navegador
      // en lugar de descargar. Usamos un iframe oculto con el blob URL: al
      // cargarse el PDF (mismo origen), llamamos a print() sobre su
      // contentWindow. Es la tecnica estandar cross-browser.
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          // Fallback: abrir en pestana nueva si el navegador bloquea el print
          // desde iframe (Safari en algunas versiones).
          window.open(url, '_blank');
        }
      };
      document.body.appendChild(iframe);
      // Liberamos memoria tras 60s (suficiente para que el usuario imprima)
      setTimeout(() => {
        try { URL.revokeObjectURL(url); iframe.remove(); } catch (_) { /* noop */ }
      }, 60000);
    } catch (e) {
      toast.error(lang === 'es' ? 'Error al generar el justificante' : 'Error generating certificate');
    } finally {
      setDownloadingJustId(null);
    }
  };

  const handleSendEmail = async () => {
    const eligible = appointments.filter(a => a.status === 'citada' && a.visit_code !== 'QSALUD TFNO');
    if (eligible.length === 0) return;
    const ok = window.confirm(
      lang === 'es'
        ? `Se enviara un email al voluntario (o titular de su familia si es dependiente) con sus ${eligible.length} cita(s) y un archivo .ics para anadirlas a su calendario. Continuar?`
        : `An email will be sent to the volunteer (or family holder if dependent) with their ${eligible.length} appointment(s) and a .ics file. Continue?`
    );
    if (!ok) return;
    // Group by study_id and call the endpoint once per study. Las citas
    // Qsalud (study_id null/undefined) se agrupan bajo la clave '__qsalud__'
    // y se envian con study_id=null al endpoint.
    const byStudy = {};
    eligible.forEach(a => {
      const key = a.study_id || '__qsalud__';
      if (!byStudy[key]) byStudy[key] = [];
      byStudy[key].push(a);
    });
    setSendingEmail(true);
    const results = [];
    await Promise.all(Object.entries(byStudy).map(async ([sid, appts]) => {
      const realSid = sid === '__qsalud__' ? null : sid;
      try {
        const { data } = await axios.post(
          `${API_URL}/api/agenda/send-appointments-email`,
          {
            volunteer_id: volunteerId,
            study_id: realSid,
            appointment_ids: appts.map(a => a.appointment_id),
          },
          { withCredentials: true },
        );
        results.push({ study_id: sid, ok: true, count: data.count, recipients: data.recipients || [] });
      } catch (e) {
        results.push({ study_id: sid, ok: false, error: e?.response?.data?.detail || 'Error' });
      }
    }));
    setSendingEmail(false);
    const okResults = results.filter(r => r.ok);
    const koResults = results.filter(r => !r.ok);
    const totalSent = okResults.reduce((s, r) => s + (r.count || 0), 0);
    const recipientsSet = new Set();
    okResults.forEach(r => (r.recipients || []).forEach(x => recipientsSet.add(x)));
    if (okResults.length > 0) {
      toast.success(
        lang === 'es'
          ? `${totalSent} cita(s) enviadas a ${Array.from(recipientsSet).join(', ')} (${okResults.length} estudio(s))`
          : `${totalSent} appointment(s) sent to ${Array.from(recipientsSet).join(', ')} (${okResults.length} study/ies)`
      );
    }
    koResults.forEach(r => {
      const code = r.study_id === '__qsalud__' ? 'QSALUD' : (studies[r.study_id]?.code || r.study_id);
      toast.error(`${code}: ${r.error}`);
    });
  };

  const cancelledCount = appointments.filter(a => a.status === 'anulada').length;

  // URS-F251: cambio de estado inline desde la pestana Citas del voluntario.
  // Util cuando una cita quedo erroneamente en 'anulada' (p.ej. tras una
  // anulacion masiva) y hay que recuperarla sin volver a la Agenda.
  const handleStatusChange = async (apptId, newStatus) => {
    const prev = appointments.find(a => a.appointment_id === apptId);
    if (!prev || prev.status === newStatus) return;
    setSavingStatus(s => ({ ...s, [apptId]: true }));
    // Optimistic update
    setAppointments(list => list.map(a => a.appointment_id === apptId ? { ...a, status: newStatus } : a));
    try {
      await axios.put(`${API_URL}/api/appointments/${apptId}`, { status: newStatus }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Estado de cita actualizado' : 'Appointment status updated');
      // URS-F307: avisar al padre (Agenda) cuando la ficha esta embebida en iframe
      // para que refresque las citas inmediatamente. Si no estamos embebidos,
      // window.parent === window y el postMessage no hace nada.
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({
            type: 'appointment_status_changed',
            appointment_id: apptId,
            status: newStatus,
            study_id: prev.study_id,
            volunteer_id: prev.volunteer_id,
            at: new Date().toISOString(),
          }, '*');
        }
      } catch { /* postMessage to cross-origin parent can throw; ignore */ }
    } catch (err) {
      // Revert on failure
      setAppointments(list => list.map(a => a.appointment_id === apptId ? { ...a, status: prev.status } : a));
      const msg = err?.response?.data?.detail || (lang === 'es' ? 'Error al actualizar el estado' : 'Failed to update status');
      toast.error(typeof msg === 'string' ? msg : (lang === 'es' ? 'Error al actualizar el estado' : 'Failed to update status'));
    } finally {
      setSavingStatus(s => { const c = { ...s }; delete c[apptId]; return c; });
    }
  };

  const handleDeleteAppt = async () => {
    if (!apptToDelete) return;
    setDeletingAppt(true);
    try {
      await axios.delete(`${API_URL}/api/appointments/${apptToDelete.appointment_id}`, { withCredentials: true });
      setAppointments(list => list.filter(a => a.appointment_id !== apptToDelete.appointment_id));
      toast.success(lang === 'es' ? 'Cita eliminada' : 'Appointment deleted');
      // Avisar al padre (Agenda) si la ficha esta embebida, para refrescar.
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({
            type: 'appointment_deleted',
            appointment_id: apptToDelete.appointment_id,
            study_id: apptToDelete.study_id,
            volunteer_id: apptToDelete.volunteer_id,
            at: new Date().toISOString(),
          }, '*');
        }
      } catch { /* ignore cross-origin */ }
      setApptToDelete(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || (lang === 'es' ? 'Error al eliminar la cita' : 'Error deleting appointment');
      toast.error(typeof msg === 'string' ? msg : (lang === 'es' ? 'Error al eliminar la cita' : 'Error deleting appointment'));
    } finally {
      setDeletingAppt(false);
    }
  };

  // Cancelled rows are hidden by default unless the toggle is ON OR the user
  // explicitly picks "anulada" in the status filter.
  // Sorted DESC by date+time (most recent first). URS-F279
  const filtered = (() => {
    const byStatus = filterStatus === 'all' ? appointments : appointments.filter(a => a.status === filterStatus);
    const visible = (filterStatus === 'anulada' || showCancelled) ? byStatus : byStatus.filter(a => a.status !== 'anulada');
    return [...visible].sort((a, b) => {
      const ka = `${a.date || ''} ${a.time || '00:00'}`;
      const kb = `${b.date || ''} ${b.time || '00:00'}`;
      return kb.localeCompare(ka);
    });
  })();

  if (!volunteerId) return <p className="text-sm text-stone-500 text-center py-8">{lang === 'es' ? 'Guarda el voluntario primero' : 'Save the volunteer first'}</p>;

  if (loading) return <div className="p-12 text-center text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>;

  if (appointments.length === 0) return (
    <div className="space-y-3">
      <div className="flex justify-start gap-2">
        <Button data-testid="citar-qsalud-btn" onClick={() => navigate(`/agenda?new=1&volunteer=${volunteerId}&visit=Qsalud&study=none`)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-2 text-sm">
          <CalendarBlank size={14} />{lang === 'es' ? 'Citar Qsalud' : 'Book Qsalud'}
        </Button>
        <Button data-testid="citar-qsalud-tfno-btn" onClick={() => navigate(`/agenda?new=1&volunteer=${volunteerId}&visit=${encodeURIComponent('QSALUD TFNO')}&study=none`)} variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-sm gap-2 text-sm">
          <Phone size={14} />{lang === 'es' ? 'Citar QSALUD TFNO' : 'Book QSALUD TFNO'}
        </Button>
      </div>
      <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
        <CalendarBlank size={40} className="text-stone-300 mx-auto mb-3" />
        <p className="text-sm text-stone-500">{lb.no_appts}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="list" data-testid="vol-appts-subtab-list">
            {lang === 'es' ? 'Listado' : 'List'}
          </TabsTrigger>
          <TabsTrigger value="ratio" data-testid="vol-appts-subtab-ratio">
            {lang === 'es' ? 'Ratio de asistencia' : 'Attendance ratio'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-3 space-y-3">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Button data-testid="citar-qsalud-btn" onClick={() => navigate(`/agenda?new=1&volunteer=${volunteerId}&visit=Qsalud&study=none`)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-2 text-sm">
            <CalendarBlank size={14} />{lang === 'es' ? 'Citar Qsalud' : 'Book Qsalud'}
          </Button>
          <Button data-testid="citar-qsalud-tfno-btn" onClick={() => navigate(`/agenda?new=1&volunteer=${volunteerId}&visit=${encodeURIComponent('QSALUD TFNO')}&study=none`)} variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-sm gap-2 text-sm">
            <Phone size={14} />{lang === 'es' ? 'Citar QSALUD TFNO' : 'Book QSALUD TFNO'}
          </Button>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="appt-status-filter" className="w-44 bg-white border-stone-200 h-9 text-sm">
              <SelectValue placeholder={lb.filter_by} />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filterStatus !== 'all' && (
            <Badge variant="outline" className="text-xs">{filtered.length} / {appointments.length}</Badge>
          )}
          {cancelledCount > 0 && (
            <button
              type="button"
              data-testid="vol-appts-toggle-cancelled"
              onClick={() => setShowCancelled(p => !p)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs font-medium transition-colors ${showCancelled ? 'bg-stone-700 text-white border-stone-700' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${showCancelled ? 'bg-white' : 'bg-stone-300'}`} />
              {lang === 'es' ? `Mostrar anuladas (${cancelledCount})` : `Show cancelled (${cancelledCount})`}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="email-appointments-btn" onClick={handleSendEmail} disabled={emailableCount === 0 || sendingEmail} className="rounded-sm gap-2 text-sm border-[#1f3e80] text-[#1f3e80] hover:bg-[#1f3e80]/5">
            <EnvelopeSimple size={14} />
            {sendingEmail
              ? (lang === 'es' ? 'Enviando...' : 'Sending...')
              : `${lb.email} (${emailableCount})`}
          </Button>
          <Button variant="outline" data-testid="print-appointments-btn" onClick={handlePrint} disabled={scheduledCount === 0} className="rounded-sm gap-2 text-sm">
            <Printer size={14} />{lb.print} ({scheduledCount})
          </Button>
          <Button
            variant="outline"
            data-testid="justificante-btn"
            onClick={() => setShowJustDialog(true)}
            disabled={appointments.length === 0}
            className="rounded-sm gap-2 text-sm border-[#1f3e80] text-[#1f3e80] hover:bg-[#1f3e80]/5"
          >
            <ClipboardText size={14} weight="duotone" />
            {lb.justificante}
          </Button>
        </div>
      </div>
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-28">{lb.date}</TableHead>
            <TableHead className="w-20">{lb.time}</TableHead>
            <TableHead>{lb.study}</TableHead>
            <TableHead className="w-24">{lb.visit}</TableHead>
            <TableHead className="w-28">{lb.status}</TableHead>
            <TableHead className="w-24 text-center">{lang === 'es' ? 'Acciones' : 'Actions'}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map(a => {
            const study = studies[a.study_id];
            return (
              <TableRow key={a.appointment_id} data-testid={`vol-appt-${a.appointment_id}`} className={`hover:bg-stone-50 ${a.status === 'anulada' ? 'opacity-60' : ''}`}>
                <TableCell className="text-sm font-mono text-stone-800">{a.date}</TableCell>
                <TableCell className="text-sm font-mono text-stone-800">{a.time || '-'}</TableCell>
                <TableCell>
                  {a.study_id && a.visit_code !== 'Qsalud' ? (
                    <button onClick={() => navigate(`/studies/${a.study_id}`)} className="text-sm font-medium text-[#1f3e80] hover:underline">
                      {study?.title || study?.code || a.study_id}
                    </button>
                  ) : (
                    <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200 gap-1 whitespace-nowrap">{a.visit_code === 'QSALUD TFNO' && <Phone size={11} />}{a.visit_code === 'QSALUD TFNO' ? 'QSALUD TFNO' : 'Qsalud'}</Badge>
                  )}
                </TableCell>
                <TableCell><Badge variant="outline" className="text-xs font-mono bg-stone-50 whitespace-nowrap">{a.visit_code}</Badge></TableCell>
                <TableCell>
                  <Select
                    value={a.status}
                    onValueChange={(v) => handleStatusChange(a.appointment_id, v)}
                    disabled={!!savingStatus[a.appointment_id]}
                  >
                    <SelectTrigger
                      data-testid={`vol-appt-status-${a.appointment_id}`}
                      className={`h-7 px-2 py-0 text-xs w-full ${apptStatusColors[a.status] || ''}`}
                    >
                      <SelectValue>{lb[a.status] || a.status}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.filter(s => s.value !== 'all').map(s => (
                        <SelectItem key={s.value} value={s.value} data-testid={`vol-appt-status-opt-${a.appointment_id}-${s.value}`}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-stone-500 hover:text-[#1f3e80]"
                      onClick={() => navigate(`/agenda?edit=${a.appointment_id}&from=volunteer:${volunteerId}`)}
                      data-testid={`vol-appt-edit-${a.appointment_id}`}
                      title={lang === 'es' ? 'Editar cita en Agenda' : 'Edit appointment in Agenda'}
                    >
                      <PencilSimple size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-stone-500 hover:text-red-600"
                      onClick={() => setApptToDelete(a)}
                      data-testid={`vol-appt-delete-${a.appointment_id}`}
                      title={lang === 'es' ? 'Borrar cita' : 'Delete appointment'}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
        </TabsContent>

        <TabsContent value="ratio" className="mt-3">
          <AttendanceRatioPanel appointments={appointments} studies={studies} lang={lang} />
        </TabsContent>
      </Tabs>

      {/* URS-F390: Dialog para elegir la cita del voluntario y descargar el
          Justificante de asistencia en PDF. */}
      <Dialog open={showJustDialog} onOpenChange={setShowJustDialog}>        <DialogContent className="max-w-2xl" data-testid="justificante-dialog">
          <DialogHeader>
            <DialogTitle>{lb.justificante}</DialogTitle>
            <DialogDescription>
              {lang === 'es'
                ? 'Selecciona la cita del voluntario para generar el justificante y abrir el diálogo de impresión.'
                : 'Select the appointment to generate the certificate and open the print dialog.'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {appointments.length === 0 ? (
              <p className="text-sm text-stone-500 py-4">{lb.no_appts}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">{lb.date}</TableHead>
                    <TableHead className="w-20">{lb.time}</TableHead>
                    <TableHead>{lb.study}</TableHead>
                    <TableHead className="w-24">{lb.visit}</TableHead>
                    <TableHead className="w-32 text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* URS-F390: ordenar por fecha+hora descendente (mas recientes primero). */}
                  {[...appointments].sort((a, b) => {
                    const ka = `${a.date || ''} ${a.time || ''}`;
                    const kb = `${b.date || ''} ${b.time || ''}`;
                    return kb.localeCompare(ka);
                  }).map(a => (
                    <TableRow key={a.appointment_id} data-testid={`just-row-${a.appointment_id}`}>
                      <TableCell className="text-sm font-mono">{a.date}</TableCell>
                      <TableCell className="text-sm font-mono">{a.time || '-'}</TableCell>
                      <TableCell className="text-sm">{studies[a.study_id]?.title || studies[a.study_id]?.code || (a.visit_code === 'Qsalud' ? 'Q-Salud' : a.visit_code === 'QSALUD TFNO' ? 'Q-Salud Tfno' : a.study_id)}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs font-mono whitespace-nowrap">{a.visit_code}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`just-download-${a.appointment_id}`}
                          disabled={downloadingJustId === a.appointment_id}
                          onClick={() => downloadJustificante(a.appointment_id, a.date)}
                          className="gap-1.5 text-xs"
                        >
                          <ClipboardText size={12} />
                          {downloadingJustId === a.appointment_id
                            ? (lang === 'es' ? 'Generando...' : 'Generating...')
                            : lb.justificante_row}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!apptToDelete}
        onOpenChange={(v) => { if (!v) setApptToDelete(null); }}
        title={lang === 'es' ? 'Borrar cita' : 'Delete appointment'}
        description={lang === 'es'
          ? 'Se va a borrar esta cita en la agenda, ¿está seguro?'
          : 'This appointment will be deleted from the agenda. Are you sure?'}
        onConfirm={handleDeleteAppt}
        loading={deletingAppt}
      />
    </div>
  );
}

const studyStatusColors = {
  activo: 'bg-green-50 text-green-700 border-green-200',
  finalizado: 'bg-amber-50 text-amber-700 border-amber-200',
};

const studyStatusLabels = {
  es: { activo: 'Activo', finalizado: 'Finalizado' },
  en: { activo: 'Active', finalizado: 'Completed' },
};

function VolunteerStudiesPanel({ volunteerId, volunteerName, volunteerEmail, lang }) {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [studies, setStudies] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [conflictStudy, setConflictStudy] = useState(null);
  const [conflictData, setConflictData] = useState(null);
  // URS-F495: dialogo envio HIP tras asignacion.
  const [hipDialog, setHipDialog] = useState(null); // {studyId, studyCode}

  const lb = lang === 'es'
    ? { active_title: 'Estudios activos', finished_title: 'Estudios finalizados', code: 'Código', title: 'Título', status: 'Estado', vcode: 'V-Code', role: 'Estado voluntario', no_active: 'No participa en estudios activos', no_finished: 'No ha participado en estudios finalizados', no_studies: 'Este voluntario no está asignado a ningún estudio',
        assign_to_study: 'Asignar a Estudio', dlg_title: 'Asignar voluntario a un estudio', dlg_desc: 'Selecciona un estudio activo. El voluntario entrará en estado Pendiente.', search_placeholder: 'Buscar por código o título...', no_match: 'No hay estudios activos que coincidan', already_assigned: 'El voluntario ya está asignado a este estudio',
        conflict_title: 'Conflicto detectado', conflict_msg: 'Este voluntario ya está participando activamente en:', conflict_accept: 'Asignar igualmente', conflict_reject: 'Cancelar', assign_success: 'Voluntario asignado correctamente', assign: 'Asignar' }
    : { active_title: 'Active studies', finished_title: 'Finished studies', code: 'Code', title: 'Title', status: 'Status', vcode: 'V-Code', role: 'Volunteer status', no_active: 'Not participating in active studies', no_finished: 'Has not participated in finished studies', no_studies: 'This volunteer is not assigned to any study',
        assign_to_study: 'Assign to Study', dlg_title: 'Assign volunteer to a study', dlg_desc: 'Select an active study. The volunteer will enter as Pending.', search_placeholder: 'Search by code or title...', no_match: 'No active studies matching', already_assigned: 'Volunteer already assigned to this study',
        conflict_title: 'Conflict detected', conflict_msg: 'This volunteer is already actively participating in:', conflict_accept: 'Assign anyway', conflict_reject: 'Cancel', assign_success: 'Volunteer assigned successfully', assign: 'Assign' };

  const loadData = () => {
    if (!volunteerId) { setLoading(false); return; }
    Promise.all([
      axios.get(`${API_URL}/api/studies`, { withCredentials: true }),
    ]).then(([sr]) => {
      const studyMap = {};
      sr.data.forEach(s => { studyMap[s.study_id] = s; });
      setStudies(studyMap);
      return axios.get(`${API_URL}/api/volunteers/${volunteerId}/studies`, { withCredentials: true });
    }).then(res => {
      setAssignments(res.data || []);
    }).catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volunteerId]);

  if (!volunteerId) return <p className="text-sm text-stone-500 text-center py-8">{lang === 'es' ? 'Guarda el voluntario primero' : 'Save the volunteer first'}</p>;
  if (loading) return <div className="p-12 text-center text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>;

  // URS-F344: un estudio se considera "finalizado" para el voluntario si:
  //   - el estudio ya esta cerrado globalmente (status='finalizado'), O
  //   - el voluntario tiene un status terminal en ese estudio
  //     (completado / excluido / abandono / retirado / no_apto).
  // Asi un voluntario que ya termino su ultima visita ve el estudio en
  // "Estudios finalizados" aunque el estudio siga activo para el resto.
  const VOL_TERMINAL = ['completado', 'excluido', 'abandono', 'retirado', 'no_apto'];
  const activeStudies = assignments.filter(a => {
    const s = studies[a.study_id];
    if (!s) return false;
    if (s.status !== 'activo') return false;
    if (VOL_TERMINAL.includes(a.status)) return false;
    return true;
  });
  const finishedStudies = assignments.filter(a => {
    const s = studies[a.study_id];
    if (!s) return false;
    return s.status === 'finalizado' || VOL_TERMINAL.includes(a.status);
  });

  // Studies available to assign: active + not yet assigned to this volunteer
  const assignedIds = new Set(assignments.map(a => a.study_id));
  const allActive = Object.values(studies).filter(s => s.status === 'activo' && !assignedIds.has(s.study_id));
  const q = assignSearch.trim().toLowerCase();
  const availableStudies = q
    ? allActive.filter(s => (s.code || '').toLowerCase().includes(q) || (s.title || '').toLowerCase().includes(q))
    : allActive;

  const doAssign = async (studyId, force) => {
    setAssigning(true);
    try {
      await axios.post(`${API_URL}/api/studies/${studyId}/volunteers`,
        { volunteer_id: volunteerId, force: !!force },
        { withCredentials: true }
      );
      toast.success(lb.assign_success);
      setShowAssignDialog(false);
      setConflictStudy(null);
      setConflictData(null);
      setAssignSearch('');
      // URS-F495: abrir dialogo HIP; navegar al estudio SOLO al cerrarlo.
      const st = studies[studyId] || {};
      setHipDialog({ studyId, studyCode: st.code || '' });
    } catch (err) {
      toast.error(errText(err));
    }
    setAssigning(false);
  };

  const closeHipDialog = () => {
    const target = hipDialog?.studyId;
    setHipDialog(null);
    if (target) navigate(`/studies/${target}?tab=volunteers`);
  };

  const handleAssignClick = async (study) => {
    try {
      const { data: check } = await axios.get(
        `${API_URL}/api/studies/${study.study_id}/volunteers/check/${volunteerId}`,
        { withCredentials: true }
      );
      if (check.conflict === 'same_study') {
        toast.error(lb.already_assigned);
        return;
      }
      if (check.conflict === 'other_study') {
        setConflictStudy(study);
        setConflictData(check.active_studies);
        return;
      }
      await doAssign(study.study_id, false);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const AssignDialogs = (
    <>
      <Dialog open={showAssignDialog && !conflictStudy} onOpenChange={o => { if (!o) { setShowAssignDialog(false); setAssignSearch(''); } }}>
        <DialogContent data-testid="assign-to-study-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flask size={18} className="text-[#1f3e80]" />
              {lb.dlg_title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500 -mt-2">{lb.dlg_desc}</p>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              data-testid="assign-study-search"
              autoFocus
              value={assignSearch}
              onChange={e => setAssignSearch(e.target.value)}
              placeholder={lb.search_placeholder}
              className="pl-9 bg-white border-stone-200"
            />
          </div>
          <div className="max-h-[380px] overflow-y-auto border border-stone-200 rounded-sm divide-y divide-stone-100 bg-white">
            {availableStudies.length === 0 ? (
              <p className="p-8 text-center text-sm text-stone-400">{lb.no_match}</p>
            ) : availableStudies.map(s => (
              <button
                key={s.study_id}
                type="button"
                data-testid={`assign-study-row-${s.study_id}`}
                onClick={() => handleAssignClick(s)}
                disabled={assigning}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 text-left transition-colors disabled:opacity-50 min-w-0"
              >
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <span className="font-mono text-xs text-[#1f3e80] font-medium truncate">{s.code}</span>
                  <span className="text-sm text-stone-800 truncate">{s.title}</span>
                </div>
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">{lang === 'es' ? 'Activo' : 'Active'}</Badge>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssignDialog(false); setAssignSearch(''); }} className="rounded-sm">
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!conflictStudy} onOpenChange={o => { if (!o) { setConflictStudy(null); setConflictData(null); } }}>
        <DialogContent className="max-w-lg w-full overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <WarningCircle size={20} weight="fill" className="text-amber-600 shrink-0" />
              {lb.conflict_title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-700">{lb.conflict_msg}</p>
          <ul className="bg-stone-50 border border-stone-200 rounded-sm divide-y divide-stone-100 max-h-[50vh] overflow-y-auto">
            {(conflictData || []).map(cs => (
              <li key={cs.study_id} className="px-3 py-2 flex items-center gap-2 text-xs min-w-0">
                <span className="font-mono text-[#1f3e80] font-medium shrink-0 max-w-[40%] truncate">{cs.study_code}</span>
                <span className="flex-1 min-w-0 truncate text-stone-600">{cs.study_title}</span>
                {cs.study_volunteer_code && <Badge variant="outline" className="font-mono text-[10px] shrink-0">{cs.study_volunteer_code}</Badge>}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConflictStudy(null); setConflictData(null); }} className="rounded-sm">
              {lb.conflict_reject}
            </Button>
            <Button
              data-testid="assign-conflict-accept"
              disabled={assigning}
              onClick={() => conflictStudy && doAssign(conflictStudy.study_id, true)}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
            >
              {assigning ? '...' : lb.conflict_accept}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URS-F495: dialogo envio HIP tras asignar el voluntario. */}
      <SendInfoSheetOnAssignDialog
        open={!!hipDialog}
        onClose={closeHipDialog}
        studyId={hipDialog?.studyId}
        studyCode={hipDialog?.studyCode}
        volunteerId={volunteerId}
        volunteerName={volunteerName || ''}
        volunteerEmail={volunteerEmail || ''}
        lang={lang}
      />
    </>
  );

  if (assignments.length === 0) return (
    <div className="space-y-4" data-testid="volunteer-studies-panel">
      <div className="flex justify-end">
        <Button data-testid="assign-to-study-btn" onClick={() => setShowAssignDialog(true)} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <Plus size={14} /> {lb.assign_to_study}
        </Button>
      </div>
      <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
        <Flask size={40} className="text-stone-300 mx-auto mb-3" />
        <p className="text-sm text-stone-500">{lb.no_studies}</p>
      </div>
      {AssignDialogs}
    </div>
  );

  const StudyTable = ({ items, emptyMsg }) => {
    if (items.length === 0) return (
      <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
        <p className="text-sm text-stone-400">{emptyMsg}</p>
      </div>
    );
    return (
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-28">{lb.code}</TableHead>
              <TableHead>{lb.title}</TableHead>
              <TableHead className="w-28">{lb.status}</TableHead>
              <TableHead className="w-20">{lb.vcode}</TableHead>
              <TableHead className="w-28">{lb.role}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(a => {
              const s = studies[a.study_id];
              if (!s) return null;
              const volStatusLabel = lang === 'es'
                ? { pendiente: 'Pendiente', apto: 'Apto', incluido: 'Incluido', excluido: 'Excluido', no_apto: 'No apto', abandono: 'Abandono', completado: 'Completado' }
                : { pendiente: 'Pending', apto: 'Eligible', incluido: 'Included', excluido: 'Excluded', no_apto: 'Not eligible', abandono: 'Withdrawal', completado: 'Completed' };
              return (
                <TableRow key={a.study_id} data-testid={`vol-study-${a.study_id}`} className="hover:bg-stone-50 cursor-pointer" onClick={() => navigate(`/studies/${a.study_id}`)}>
                  <TableCell className="font-mono text-xs text-[#1f3e80] font-medium">{s.code}</TableCell>
                  <TableCell className="text-sm text-stone-900">{s.title}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${studyStatusColors[s.status] || ''}`}>
                      {studyStatusLabels[lang]?.[s.status] || s.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm font-bold text-[#1f3e80]">{a.study_volunteer_code || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{volStatusLabel[a.status] || a.status}</Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="volunteer-studies-panel">
      <div className="flex justify-end">
        <Button data-testid="assign-to-study-btn" onClick={() => setShowAssignDialog(true)} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <Plus size={14} /> {lb.assign_to_study}
        </Button>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          {lb.active_title}
          {activeStudies.length > 0 && <Badge variant="outline" className="text-xs ml-1">{activeStudies.length}</Badge>}
        </h3>
        <StudyTable items={activeStudies} emptyMsg={lb.no_active} />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          {lb.finished_title}
          {finishedStudies.length > 0 && <Badge variant="outline" className="text-xs ml-1">{finishedStudies.length}</Badge>}
        </h3>
        <StudyTable items={finishedStudies} emptyMsg={lb.no_finished} />
      </div>
      {AssignDialogs}
    </div>
  );
}

/**
 * Banner that appears at the top of the volunteer's Documents tab when
 * there are VIDsigner documents waiting for signature/return.
 *
 * - Shows the number of pending docs (sent to tablet but not yet
 *   archived back into the volunteer profile).
 * - Provides a "Sincronizar" button that forces a refresh against the
 *   Validated ID API and downloads any signed PDF.
 * - Auto-polls every 15s while there are pending docs (max 20 polls = 5
 *   minutes) so the user does not have to click anything if the
 *   webhook is unreachable.
 */
function TabletPendingRow({ d, lang, onCancelled }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const es = lang === 'es';
  const cancel = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/vidsigner/documents/${d.doc_gui}/cancel`, {}, { withCredentials: true });
      toast.success(es ? 'Envío a tableta anulado' : 'Tablet request cancelled');
      onCancelled();
    } catch (err) {
      toast.error(errText(err, es ? 'No se pudo anular' : 'Could not cancel'));
    }
    setBusy(false); setConfirming(false);
  };
  const sent = d.sent_at ? new Date(d.sent_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '';
  return (
    <li className="flex items-center gap-2 text-xs text-violet-900" data-testid={`tablet-pending-${d.doc_gui}`}>
      <span className="flex-1 truncate"><b>{d.document_name}</b> — {es ? 'enviado' : 'sent'} {sent}{d.device_name ? ` · ${d.device_name}` : ''}</span>
      {d.doc_kind === 'study_ci' ? (
        <span className="text-[11px] text-violet-600">{es ? 'Consentimiento de estudio: se gestiona desde el estudio' : 'Study consent: manage from the study'}</span>
      ) : confirming ? (
        <>
          <span className="text-red-700">{es ? '¿Anular el envío?' : 'Cancel request?'}</span>
          <Button size="sm" disabled={busy} onClick={cancel} className="h-7 bg-red-600 hover:bg-red-700 text-white" data-testid={`tablet-cancel-confirm-${d.doc_gui}`}>{es ? 'Sí, anular' : 'Yes'}</Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirming(false)} className="h-7">{es ? 'No' : 'No'}</Button>
        </>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setConfirming(true)} className="h-7 gap-1 text-red-600 hover:text-red-700" data-testid={`tablet-cancel-${d.doc_gui}`}>
          <X size={12} />{es ? 'Anular' : 'Cancel'}
        </Button>
      )}
    </li>
  );
}

function VidsignerSyncBanner({ pending, syncing, onSync, onPoll, onCancelled, lang }) {
  const count = pending?.length || 0;

  // Auto-poll every 15s while there are pending docs (cap at 20 attempts
  // to avoid hammering Validated ID forever if VIDsigner returns the
  // same Pending status indefinitely).
  useEffect(() => {
    if (!count) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      await onPoll();
    };
    const handle = setInterval(() => {
      if (attempts >= 20) {
        clearInterval(handle);
        return;
      }
      tick();
    }, 15000);
    return () => clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  // URS-F245: cuando no hay pendientes mostramos un chip compacto con el boton
  // "Sincronizar firmas" para que el operador pueda forzar el polling manualmente
  // sin depender de la auto-comprobacion (util si VIDsigner tardo en notificar).
  if (!count) {
    return (
      <div
        className="bg-stone-50 border border-stone-200 rounded-sm px-3 py-2 flex items-center justify-between"
        data-testid="vidsigner-sync-banner-idle"
      >
        <div className="flex items-center gap-2 text-xs text-stone-600">
          <FingerprintSimple size={16} className="text-violet-600" />
          <span>{lang === 'es' ? 'Sin firmas pendientes. Si esperas una firma reciente, fuerza la sincronizacion con VIDsigner.' : 'No pending signatures. If you are waiting for a recent signature, force a sync with VIDsigner.'}</span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSync}
          disabled={syncing}
          className="border-violet-300 text-violet-700 hover:bg-violet-50 rounded-sm gap-2 h-8"
          data-testid="vidsigner-sync-btn"
        >
          <FingerprintSimple size={14} />
          {syncing
            ? (lang === 'es' ? 'Sincronizando...' : 'Syncing...')
            : (lang === 'es' ? 'Sincronizar firmas' : 'Sync signatures')}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-violet-50 border border-violet-200 rounded-sm p-4 space-y-3" data-testid="vidsigner-sync-banner">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <FingerprintSimple size={22} weight="duotone" className="text-violet-600" />
        <div>
          <p className="text-sm font-semibold text-violet-900">
            {lang === 'es'
              ? `${count} documento(s) enviado(s) a firmar a la tableta`
              : `${count} document(s) sent to tablet for signature`}
          </p>
          <p className="text-xs text-violet-700">
            {lang === 'es'
              ? 'Comprobando con VIDsigner cada 15s. Tambien puedes pulsar Sincronizar manualmente cuando termine de firmarse.'
              : 'Polling VIDsigner every 15s. You can also click Sync manually once the tablet signature is complete.'}
          </p>
        </div>
      </div>
      <Button
        type="button"
        onClick={onSync}
        disabled={syncing}
        className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm gap-2"
        data-testid="vidsigner-sync-btn"
      >
        <FingerprintSimple size={16} />
        {syncing
          ? (lang === 'es' ? 'Sincronizando...' : 'Syncing...')
          : (lang === 'es' ? 'Sincronizar firmas' : 'Sync signatures')}
      </Button>
    </div>
      <ul className="space-y-1 pl-9">{pending.map(d => <TabletPendingRow key={d.doc_gui} d={d} lang={lang} onCancelled={onCancelled} />)}</ul>
    </div>
  );
}




export default function VolunteerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const { can } = usePermissions();
  // URS-F348: la pestaña Q Salud se gateara por el permiso transversal `qsalud`.
  const canSeeQsalud = can('qsalud', 'view');
  const isAdmin = (user?.roles || (user?.role ? [user.role] : [])).includes('admin');
  const isEdit = !!id;
  const [showVolAudit, setShowVolAudit] = useState(false);
  // When embedded inside an iframe (Agenda "Revisar Qsalud" flow) we hide
  // the page chrome and add a small confirmation header.
  const embedded = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embedded') === '1';
  const initialTab = typeof window !== 'undefined' ? (new URLSearchParams(window.location.search).get('tab') || 'personal') : 'personal';
  // URS-F420b: `only_tab=health` (o cualquier tab) restringe la UI para que
  // el usuario solo vea la pestana especificada, sin poder navegar a otras.
  // Se usa desde el popup "Ver Qsalud" del panel de voluntarios incluidos.
  const onlyTab = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('only_tab') : null;
  const [activeTab, setActiveTab] = useState(onlyTab || initialTab);

  const [fields, setFields] = useState([]);
  const [personalData, setPersonalData] = useState({});
  const [healthData, setHealthData] = useState({});
  const [status, setStatus] = useState('active');
  // URS-F429: timestamp de ultima actualizacion del cuestionario Q Salud.
  // Se refresca cada vez que se recarga el voluntario y se muestra a la
  // derecha de la fila de pestañas.
  const [qsaludLastReviewedAt, setQsaludLastReviewedAt] = useState(null);
  const [documents, setDocuments] = useState({
    rgpd: { file_id: null, file_name: null, signed: false, uploaded_at: null },
    marketing: { file_id: null, file_name: null, signed: false, uploaded_at: null },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [consentTemplates, setConsentTemplates] = useState([]);
  const [isMinor, setIsMinor] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  // URS-F256: Tutor / Representante legal. Datos del firmante cuando el
  // voluntario es menor de edad o tiene capacidad disminuida.
  const [legalGuardian, setLegalGuardian] = useState({
    enabled: false, relacion: 'Tutor legal', motivo: '',
    nombre: '', apellidos: '', dni: '', email: '',
  });
  const guardianFirstAutoToggled = useRef(false);
  // Auto-activacion: cuando el usuario marca is_minor por primera vez, se
  // activa tambien el switch de tutor legal. Si desmarca minor y el motivo
  // sigue siendo "Menor de edad", se desactiva tambien para evitar
  // configuraciones incoherentes; en otros casos (capacidad disminuida) se
  // respeta la decision manual del usuario.
  useEffect(() => {
    if (isMinor) {
      if (!guardianFirstAutoToggled.current && !legalGuardian.enabled) {
        guardianFirstAutoToggled.current = true;
        setLegalGuardian(g => ({ ...g, enabled: true, motivo: g.motivo || 'Menor de edad' }));
      }
    } else if (legalGuardian.enabled && (legalGuardian.motivo === 'Menor de edad' || !legalGuardian.motivo)) {
      setLegalGuardian(g => ({ ...g, enabled: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMinor]);
  const [dupWarning, setDupWarning] = useState(null);
  const [dupBlocked, setDupBlocked] = useState(null);
  const [vsPending, setVsPending] = useState([]);   // VIDsigner docs awaiting signature
  const [vsSyncing, setVsSyncing] = useState(false);

  const reloadVolunteer = async () => {
    if (!isEdit) return;
    try {
      const { data: vol } = await axios.get(`${API_URL}/api/volunteers/${id}`, { withCredentials: true });
      setPersonalData(vol.personal_data || {});
      setHealthData(vol.health_questionnaire || {});
      setStatus(vol.status || 'active');
      setDocuments(vol.documents || {});
      setQsaludLastReviewedAt(vol.qsalud_last_reviewed_at || null);
    } catch { /* silent */ }
  };

  // Returns the list of VIDsigner docs for this volunteer that are not yet
  // archived to the volunteer record (i.e. pending or just-signed-not-yet-fetched).
  const loadVsPending = async () => {
    if (!isEdit) return [];
    try {
      const { data } = await axios.get(
        `${API_URL}/api/vidsigner/documents?volunteer_id=${encodeURIComponent(id)}&limit=20`,
        { withCredentials: true }
      );
      const items = (data?.items || []).filter(
        d => !d.archived_to_volunteer && !['cancelled', 'canceled', 'rejected', 'expired'].includes((d.status || '').toLowerCase())
      );
      setVsPending(items);
      return items;
    } catch {
      return [];
    }
  };

  // Force a server-side refresh of all pending VIDsigner docs for this
  // volunteer: queries Validated ID, downloads any signed PDF and archives
  // it into volunteer.documents. Reloads the volunteer afterwards so the
  // freshly archived doc shows up in the UI.
  const syncVidsigner = async (silent = false) => {
    if (!isEdit) return { archived: 0 };
    setVsSyncing(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/vidsigner/refresh-pending?volunteer_id=${encodeURIComponent(id)}`,
        {},
        { withCredentials: true }
      );
      const archived = r.data?.archived || 0;
      if (archived > 0) {
        await reloadVolunteer();
        toast.success(lang === 'es'
          ? `${archived} firma(s) actualizada(s) desde VIDsigner`
          : `${archived} signature(s) updated from VIDsigner`);
      } else if (!silent) {
        const det = r.data?.details || [];
        const label = { pendiente: 'pendiente de firma', error_guardando_pdf: 'firmado, pero no se pudo guardar el PDF', pdf_no_disponible: 'firmado, PDF aún no disponible', cerrado_en_vidsigner: 'rechazado/caducado en VIDsigner', no_existe_en_vidsigner: 'ya no existe en VIDsigner (retirado de pendientes)', error: 'error consultando VIDsigner' };
        const lines = det.map(d => {
          const waitingIp = (d.signers || []).some(x => x.role === 'investigator' && !/sign/i.test(x.status || ''));
          const why = d.result === 'pendiente' && waitingIp ? 'pendiente de la firma del IP' : (label[d.result] || d.result);
          return `${d.document_name}: ${why} (${d.status})`;
        });
        if (lines.length) toast.info(lines.join('\n'), { duration: 12000 });
        else toast.info(lang === 'es' ? 'Sin nuevas firmas pendientes desde VIDsigner' : 'No new pending signatures from VIDsigner');
        if ((r.data?.errors || []).length) toast.error(r.data.errors.join('\n'), { duration: 12000 });
      }
      await loadVsPending();
      return r.data || { archived: 0 };
    } catch (e) {
      if (!silent) {
        toast.error(lang === 'es' ? 'Error al sincronizar con VIDsigner' : 'Error syncing with VIDsigner');
      }
      return { archived: 0, error: true };
    } finally {
      setVsSyncing(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data: fc } = await axios.get(`${API_URL}/api/config/volunteer-fields`, { withCredentials: true });
        setFields(fc);
        const { data: ct } = await axios.get(`${API_URL}/api/consent-templates`, { withCredentials: true });
        // Defense in depth: collapse to ONE template per `type`. If the
        // DB ever returns several active templates with the same type
        // (e.g. operator created a 2nd LOPD instead of editing the
        // existing one), keep the most recently updated/created so the
        // volunteer Documents tab shows a single card per type.
        const byType = new Map();
        (ct || []).forEach((t) => {
          const key = t.type || `__${t.template_id}`;
          const ts = t.updated_at || t.created_at || '';
          const prev = byType.get(key);
          if (!prev || (prev.updated_at || prev.created_at || '') < ts) {
            byType.set(key, t);
          }
        });
        setConsentTemplates(Array.from(byType.values()));
        if (isEdit) {
          const { data: vol } = await axios.get(`${API_URL}/api/volunteers/${id}`, { withCredentials: true });
          setPersonalData(vol.personal_data || {});
          setHealthData(vol.health_questionnaire || {});
          setStatus(vol.status || 'active');
          setQsaludLastReviewedAt(vol.qsalud_last_reviewed_at || null);
          if (vol.documents) setDocuments(vol.documents);
          if (vol.personal_data?.is_minor) setIsMinor(true);
          if (vol.personal_data?.is_staff) setIsStaff(true);
          if (vol.personal_data?.legal_guardian && typeof vol.personal_data.legal_guardian === 'object') {
            setLegalGuardian(g => ({ ...g, ...vol.personal_data.legal_guardian }));
            // Si ya viene activado de BBDD, no auto-toggleamos.
            if (vol.personal_data.legal_guardian.enabled) guardianFirstAutoToggled.current = true;
          }
          // Auto-sync VIDsigner: tras cargar el voluntario, comprobamos si
          // hay documentos enviados a firmar que el backend aun no haya
          // archivado y los reclamamos a VIDsigner. Asi, si el voluntario
          // firmo en la tableta mientras el ECRD estaba cerrado, el PDF
          // firmado aparece automaticamente al abrir la ficha.
          const pending = await loadVsPending();
          if (pending.length > 0) {
            await syncVidsigner(true);  // silent=true: solo notifica si llega una firma nueva
          }
        } else {
          const { data: nn } = await axios.get(`${API_URL}/api/volunteers/next-number`, { withCredentials: true });
          setPersonalData(prev => ({ ...prev, num_voluntario: String(nn.next_number) }));
        }
      } catch { toast.error('Error loading data'); }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const doSave = async () => {
    setSaving(true);
    try {
      // URS-F256: validacion previa al guardado del tutor legal.
      if (legalGuardian.enabled) {
        const missing = [];
        if (!legalGuardian.nombre?.trim()) missing.push('nombre');
        if (!legalGuardian.apellidos?.trim()) missing.push('apellidos');
        if (!legalGuardian.dni?.trim()) missing.push('DNI');
        if (!legalGuardian.relacion?.trim()) missing.push('relacion');
        if (missing.length) {
          toast.error(lang === 'es' ? `Faltan datos del tutor legal: ${missing.join(', ')}` : `Missing legal guardian: ${missing.join(', ')}`);
          setSaving(false);
          return;
        }
      }
      const guardianPayload = legalGuardian.enabled
        ? { ...legalGuardian, enabled: true }
        : { ...legalGuardian, enabled: false };
      // URS: validar observacion obligatoria (boolean_obs) solo en la pestana activa.
      const tabsToCheck = activeTab === 'health' ? ['health_questionnaire']
        : activeTab === 'personal' ? ['personal_data']
        : ['personal_data', 'health_questionnaire'];
      const obsMissing = [];
      for (const f of (fields || [])) {
        if (f.type !== 'boolean_obs' || f.active === false) continue;
        if (!tabsToCheck.includes(f.tab)) continue;
        const src = f.tab === 'health_questionnaire' ? healthData : personalData;
        const cur = (src || {})[f.field_key];
        if (f.group_selector) continue;
        if (f.obs_required === false) continue;
        const isYes = cur && typeof cur === 'object' ? cur.value === 'si' : !!cur;
        const obs = cur && typeof cur === 'object' ? (cur.obs || '') : (typeof cur === 'string' ? cur : '');
        if (isYes && !String(obs).trim()) obsMissing.push(lang === 'es' ? f.label_es : f.label_en);
      }
      if (obsMissing.length) {
        toast.error(lang === 'es' ? `Revisa la respuesta en: ${obsMissing.join(', ')}` : `Please fill the note in: ${obsMissing.join(', ')}`);
        setSaving(false);
        return;
      }
      // URS: validar campos OBLIGATORIOS solo de la pestana activa (Datos
      // Personales o Q Salud), para no bloquear una pestana por culpa de otra.
      const isEmptyVal = (f, v) => {
        if (f.type === 'multiselect') return !Array.isArray(v) || v.length === 0;
        if (f.type === 'checkbox') return v !== true;
        if (f.type === 'boolean_obs') { const val = (v && typeof v === 'object') ? v.value : v; return !val; }
        return v === undefined || v === null || String(v).trim() === '';
      };
      const tabsToValidate = activeTab === 'health' ? ['health_questionnaire']
        : activeTab === 'personal' ? ['personal_data']
        : ['personal_data', 'health_questionnaire'];
      const missingByTab = { personal_data: [], health_questionnaire: [] };
      for (const f of (fields || [])) {
        if (!tabsToValidate.includes(f.tab) || f.active === false || !f.required) continue;
        const src = f.tab === 'health_questionnaire' ? healthData : personalData;
        if (isEmptyVal(f, (src || {})[f.field_key])) missingByTab[f.tab].push(lang === 'es' ? f.label_es : f.label_en);
      }
      if (missingByTab.personal_data.length) {
        toast.error(lang === 'es' ? `Faltan campos obligatorios de Datos Personales: ${missingByTab.personal_data.join(', ')}` : `Missing required Personal Data fields: ${missingByTab.personal_data.join(', ')}`);
        setSaving(false);
        return;
      }
      if (missingByTab.health_questionnaire.length) {
        toast.error(lang === 'es' ? `Faltan campos obligatorios del Q Salud: ${missingByTab.health_questionnaire.join(', ')}` : `Missing required Health Q fields: ${missingByTab.health_questionnaire.join(', ')}`);
        setSaving(false);
        return;
      }
      const pd = { ...personalData, is_minor: isMinor, is_staff: isStaff, legal_guardian: guardianPayload };
      const payload = { status, personal_data: pd, health_questionnaire: healthData, documents };
      if (isEdit) {
        await axios.put(`${API_URL}/api/volunteers/${id}`, payload, { withCredentials: true });
        toast.success(lang === 'es' ? 'Voluntario actualizado' : 'Volunteer updated');
        // URS-F429: refrescar timestamp de Q Salud (el backend lo actualiza al guardar).
        await reloadVolunteer();
        if (embedded && typeof window !== 'undefined' && window.parent && window.parent !== window) {
          // Notify the parent (Agenda dialog) that Qsalud has been saved.
          window.parent.postMessage({ type: 'qsalud_saved', volunteer_id: id, at: new Date().toISOString() }, '*');
        }
      } else {
        await axios.post(`${API_URL}/api/volunteers`, payload, { withCredentials: true });
        toast.success(lang === 'es' ? 'Voluntario creado' : 'Volunteer created');
        navigate('/volunteers');
      }
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  // Grupo de enfermedad: solo se exige al guardar desde la pestaña del propio campo.
  const groupProblems = () => {
    const tab = activeTab === 'health' ? 'health_questionnaire' : activeTab === 'personal' ? 'personal_data' : null;
    if (!tab) return [];
    const src = tab === 'health_questionnaire' ? healthData : personalData;
    return (fields || [])
      .filter(f => f.group_selector && f.type === 'boolean_obs' && f.active !== false && f.tab === tab)
      .map(f => { const p = groupItemsProblem((src || {})[f.field_key], f, lang); return p ? `${lang === 'es' ? f.label_es : f.label_en} (${p})` : null; })
      .filter(Boolean);
  };

  const handleSave = async () => {
    const gp = groupProblems();
    if (gp.length) { toast.error(lang === 'es' ? `Revisa la respuesta en: ${gp.join(', ')}` : `Check the answer in: ${gp.join(', ')}`); return; }
    const dni = (personalData.dni || '').trim();
    const email = (personalData.email || '').trim();
    const phone = (personalData.telefono || '').trim();
    const tp = activeTab === 'personal' ? (emailProblem(email, lang) || phoneProblem(phone, lang)) : null;
    if (tp) { toast.error(tp); return; }

    if (!dni && !email && !phone) { doSave(); return; }

    try {
      const params = new URLSearchParams();
      if (dni) params.set('dni', dni);
      if (email) params.set('email', email);
      if (phone) params.set('phone', phone);
      if (isEdit) params.set('exclude_id', id);

      const { data: dup } = await axios.get(`${API_URL}/api/volunteers/check-duplicates?${params}`, { withCredentials: true });

      if (dup.dni_exists) {
        setDupBlocked(dup.dni_volunteer);
        return;
      }

      const warnings = [];
      if (dup.email_exists) warnings.push(`${lang === 'es' ? 'Email ya registrado en' : 'Email already registered in'}: ${dup.email_volunteer}`);
      if (dup.phone_exists) warnings.push(`${lang === 'es' ? 'Teléfono ya registrado en' : 'Phone already registered in'}: ${dup.phone_volunteer}`);

      if (warnings.length > 0) {
        setDupWarning(warnings);
        return;
      }
    } catch { /* If check fails, proceed with save */ }

    doSave();
  };

  const handleFileUpload = async (docType, file) => {
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/files/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setDocuments(prev => ({ ...prev, [docType]: { ...prev[docType], file_id: data.id, file_name: data.original_filename, uploaded_at: data.created_at } }));
      toast.success(lang === 'es' ? 'Archivo subido' : 'File uploaded');
    } catch { toast.error(lang === 'es' ? 'Error al subir archivo' : 'Upload failed'); }
    setUploading(null);
  };

  const handleConsentUpload = async (docKey, file) => {
    setUploading(docKey);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/files/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const docObj = {
        file_id: data.id,
        file_name: data.original_filename,
        signed: true,
        uploaded_at: data.created_at,
        signed_at: data.created_at,
      };
      setDocuments(prev => ({ ...prev, [docKey]: docObj }));
      toast.success(lang === 'es' ? 'Documento subido y marcado como firmado' : 'Document uploaded and marked as signed');
    } catch { toast.error(lang === 'es' ? 'Error al subir archivo' : 'Upload failed'); }
    setUploading(null);
  };

  const consentDocKeys = consentTemplates.map(t => t.type);
  const signedCount = consentDocKeys.filter(k => documents[k]?.signed).length;
  const totalDocs = consentDocKeys.length || 1;
  const docStatus = consentDocKeys.length === 0 ? 'red' : signedCount === totalDocs ? 'green' : signedCount > 0 ? 'yellow' : 'red';
  const docColors = { red: 'bg-red-500', yellow: 'bg-amber-400', green: 'bg-green-500' };
  const docLabels = { es: { red: 'Sin documentos', yellow: 'Parcial', green: 'Completo' }, en: { red: 'No documents', yellow: 'Partial', green: 'Complete' } };

  const personalFields = fields.filter(f => f.tab === 'personal_data' && f.active).sort((a, b) => a.order - b.order);
  const healthFields = fields.filter(f => f.tab === 'health_questionnaire' && f.active).sort((a, b) => a.order - b.order);

  if (loading) return <div className="p-12 text-center text-stone-500 font-body">{t('loading')}</div>;

  return (
    <div className={embedded ? 'space-y-4 p-4' : 'space-y-6'} data-testid="volunteer-form">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {!embedded && (
            <Button variant="ghost" size="icon" data-testid="back-to-volunteers-btn" onClick={() => navigate('/volunteers')} className="h-8 w-8 text-stone-500 hover:text-stone-900">
              <ArrowLeft size={18} />
            </Button>
          )}
          <div>
            <h1 className={`${embedded ? 'text-lg' : 'text-2xl'} font-heading font-semibold text-stone-900`}>
              {embedded ? (lang === 'es' ? 'Revisar Qsalud' : 'Review Qsalud') : (isEdit ? t('edit_volunteer') : t('new_volunteer'))}
            </h1>
            {isEdit && <p className="text-xs font-mono text-stone-400 mt-0.5">{id}</p>}
          </div>
        </div>
        <Button data-testid="save-volunteer-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <FloppyDisk size={16} />
          {saving ? '...' : t('save')}
        </Button>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-4">
          <Label className="text-sm font-medium text-stone-800">{t('status')}:</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="volunteer-status-select" className="w-40 bg-white border-stone-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              {statusOptions.map(s => (<SelectItem key={s.value} value={s.value}>{lang === 'es' ? s.es : s.en}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="staff-toggle">
          <Switch checked={isStaff} onCheckedChange={setIsStaff} className="scale-90" />
          <span className="text-xs text-stone-600 font-medium">{lang === 'es' ? 'Personal de la casa' : 'In-house staff'}</span>
        </label>
      </div>

      <Tabs value={activeTab} className="w-full" onValueChange={async (v) => {
        setActiveTab(v);
        if (v === 'documents' && id) {
          await syncVidsigner(true);
        }
      }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <TabsList className="bg-stone-100">
              {(!onlyTab || onlyTab === 'personal') && <TabsTrigger value="personal" data-testid="tab-personal">{lang === 'es' ? 'Datos Personales' : 'Personal Data'}</TabsTrigger>}
              {canSeeQsalud && (!onlyTab || onlyTab === 'health') && <TabsTrigger value="health" data-testid="tab-health">{lang === 'es' ? 'Q Salud' : 'Health Q'}</TabsTrigger>}
              {(!onlyTab || onlyTab === 'documents') && <TabsTrigger value="documents" data-testid="tab-documents">{lang === 'es' ? 'Documentos' : 'Documents'}</TabsTrigger>}
              {(!onlyTab || onlyTab === 'appointments') && <TabsTrigger value="appointments" data-testid="tab-appointments">{lang === 'es' ? 'Citas' : 'Appointments'}</TabsTrigger>}
              {(!onlyTab || onlyTab === 'studies') && <TabsTrigger value="studies" data-testid="tab-studies">{lang === 'es' ? 'Estudios' : 'Studies'}</TabsTrigger>}
            </TabsList>
            {isEdit && isAdmin && !onlyTab && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="vol-audit-btn"
                onClick={() => setShowVolAudit(true)}
                className="rounded-sm gap-1 h-8 text-xs text-[#1f3e80] border-[#1f3e80]/40 hover:bg-[#1f3e80]/5"
                title={lang === 'es' ? 'Ver todo el historial de auditoria del voluntario' : 'View full volunteer audit timeline'}
              >
                <ClockCounterClockwise size={14} />{lang === 'es' ? 'Auditoría' : 'Audit'}
              </Button>
            )}
          </div>
          {canSeeQsalud && qsaludLastReviewedAt && (
            <span
              data-testid="qsalud-last-reviewed-label"
              className="text-xs text-stone-600 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-sm whitespace-nowrap"
              title={new Date(qsaludLastReviewedAt).toLocaleString()}
            >
              {lang === 'es' ? 'Q Salud actualizado a ' : 'Q Salud updated at '}
              <strong className="text-emerald-800">{new Date(qsaludLastReviewedAt).toLocaleDateString()}</strong>
            </span>
          )}
        </div>

        <TabsContent value="personal" className="mt-6">
          <div className="bg-white border border-stone-200 rounded-sm p-6">
            <div className="flex items-center gap-3 mb-6 p-3 border border-stone-200 rounded-sm bg-stone-50" data-testid="doc-status-indicator">
              <Label className="text-sm font-medium text-stone-800">{t('documentation')}:</Label>
              <span className={`inline-block w-3 h-3 rounded-full ${docColors[docStatus]}`} />
              <span className={`text-sm ${docStatus === 'red' ? 'text-red-600 font-medium' : docStatus === 'yellow' ? 'text-amber-600 font-medium' : 'text-green-600 font-medium'}`}>
                {docLabels[lang]?.[docStatus]}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {personalFields.map(f => {
                if (f.field_key === 'fecha_nacimiento') {
                  // URS-F387: fecha_nacimiento a mitad + Edad (calculada) a la
                  // derecha, dentro de la misma columna del grid padre.
                  return (
                    <div key={f.field_key} className="grid grid-cols-2 gap-3">
                      <DynamicField field={f} value={personalData[f.field_key]} onChange={val => setPersonalData(prev => ({ ...prev, [f.field_key]: val }))} lang={lang} />
                      <InlineAge birthDate={personalData.fecha_nacimiento} lang={lang} />
                    </div>
                  );
                }
                return (
                  <DynamicField key={f.field_key} field={f} value={personalData[f.field_key]} onChange={val => setPersonalData(prev => ({ ...prev, [f.field_key]: val }))} lang={lang} readOnly={f.field_key === 'num_voluntario'}
                    isMinor={f.field_key === 'dni' ? isMinor : undefined}
                    onMinorChange={f.field_key === 'dni' ? setIsMinor : undefined}
                  />
                );
              })}
            </div>
            {personalFields.length === 0 && (
              <p className="text-sm text-stone-400 text-center py-8">{lang === 'es' ? 'No hay campos configurados. Ve a Configuracion > Voluntarios.' : 'No fields configured. Go to Settings > Volunteers.'}</p>
            )}

            {/* URS-F256: Representacion legal (firma del tutor) */}
            <div className="mt-6 pt-5 border-t border-stone-200" data-testid="legal-guardian-section">
              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="legal-guardian-toggle">
                <Switch
                  checked={legalGuardian.enabled}
                  onCheckedChange={(v) => setLegalGuardian(g => ({ ...g, enabled: v, motivo: v ? (g.motivo || (isMinor ? 'Menor de edad' : '')) : g.motivo }))}
                  className="scale-90 mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium text-stone-800">
                    {lang === 'es' ? 'Requiere firma por representante legal' : 'Requires signature by legal guardian'}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {lang === 'es'
                      ? 'Activa esta opcion si el voluntario es menor de edad o tiene la capacidad de obrar disminuida. Los documentos (LOPD, marketing y consentimientos) se firmaran a nombre del tutor.'
                      : 'Enable if the volunteer is a minor or has diminished legal capacity. Documents (GDPR, marketing and consents) will be signed by the guardian.'}
                  </div>
                </div>
              </label>
              {legalGuardian.enabled && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-amber-50 border border-amber-200 rounded-sm">
                  <div>
                    <Label className="text-xs text-stone-700">{lang === 'es' ? 'Relacion' : 'Relation'} <span className="text-red-500">*</span></Label>
                    <Select
                      value={legalGuardian.relacion || 'Tutor legal'}
                      onValueChange={(v) => setLegalGuardian(g => ({ ...g, relacion: v }))}
                    >
                      <SelectTrigger data-testid="lg-relacion-select" className="h-9 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Madre">{lang === 'es' ? 'Madre' : 'Mother'}</SelectItem>
                        <SelectItem value="Padre">{lang === 'es' ? 'Padre' : 'Father'}</SelectItem>
                        <SelectItem value="Tutor legal">{lang === 'es' ? 'Tutor legal' : 'Legal guardian'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-700">{lang === 'es' ? 'Motivo' : 'Reason'}</Label>
                    <Select
                      value={legalGuardian.motivo || ''}
                      onValueChange={(v) => setLegalGuardian(g => ({ ...g, motivo: v }))}
                    >
                      <SelectTrigger data-testid="lg-motivo-select" className="h-9 bg-white"><SelectValue placeholder={lang === 'es' ? 'Seleccionar...' : 'Select...'} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Menor de edad">{lang === 'es' ? 'Menor de edad' : 'Minor'}</SelectItem>
                        <SelectItem value="Capacidad disminuida">{lang === 'es' ? 'Capacidad disminuida' : 'Diminished capacity'}</SelectItem>
                        <SelectItem value="Otro">{lang === 'es' ? 'Otro' : 'Other'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-700">{lang === 'es' ? 'Nombre del representante' : 'Guardian name'} <span className="text-red-500">*</span></Label>
                    <Input data-testid="lg-nombre-input" value={legalGuardian.nombre || ''} onChange={e => setLegalGuardian(g => ({ ...g, nombre: e.target.value }))} className="h-9 bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-700">{lang === 'es' ? 'Apellidos' : 'Surname'} <span className="text-red-500">*</span></Label>
                    <Input data-testid="lg-apellidos-input" value={legalGuardian.apellidos || ''} onChange={e => setLegalGuardian(g => ({ ...g, apellidos: e.target.value }))} className="h-9 bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-700">DNI / NIE <span className="text-red-500">*</span></Label>
                    <Input data-testid="lg-dni-input" value={legalGuardian.dni || ''} onChange={e => setLegalGuardian(g => ({ ...g, dni: e.target.value.toUpperCase() }))} className="h-9 bg-white font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-700">{lang === 'es' ? 'Email del tutor (opcional)' : 'Guardian email (optional)'}</Label>
                    <Input data-testid="lg-email-input" type="email" value={legalGuardian.email || ''} onChange={e => setLegalGuardian(g => ({ ...g, email: e.target.value }))} className="h-9 bg-white"
                      placeholder={lang === 'es' ? 'Para enviar firmas remotas al tutor' : 'For remote signature requests'} />
                  </div>
                </div>
              )}
            </div>
          </div>
          {isEdit && id && (
            <FamilyPanel
              volunteerId={id}
              currentVolunteerName={`${personalData.nombre || ''} ${personalData.apellidos || ''}`.trim()}
              lang={lang}
            />
          )}
          <div className="flex justify-center mt-6">
            <Button data-testid="save-volunteer-btn-personal" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
              <FloppyDisk size={16} />
              {saving ? '...' : t('save')}
            </Button>
          </div>
        </TabsContent>

        {canSeeQsalud && <TabsContent value="health" className="mt-6">
          <div className="bg-white border border-stone-200 rounded-sm p-6">
            <ComputedAge birthDate={personalData.fecha_nacimiento} lang={lang} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {healthFields.map(f => (
                <DynamicField key={f.field_key} field={f} value={healthData[f.field_key]} onChange={val => setHealthData(prev => ({ ...prev, [f.field_key]: val }))} lang={lang} />
              ))}
            </div>
            {healthFields.length === 0 && (
              <p className="text-sm text-stone-400 text-center py-8">{lang === 'es' ? 'No hay campos configurados.' : 'No fields configured.'}</p>
            )}

            {/* URS-F377: Odontograma global del voluntario. Guardado en
                health_questionnaire.dental_status. Al asignar al voluntario a
                un estudio dental, se precargara automaticamente el teeth_status
                del study_volunteer con este odontograma. */}
            <div className="mt-8 pt-6 border-t border-stone-200">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-base font-semibold text-stone-800">
                    {lang === 'es' ? 'Odontograma' : 'Dental Chart'}
                  </h3>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {lang === 'es'
                      ? 'Estado dental base del voluntario. Se cargara automaticamente al asignarlo a un estudio dental (Odontologia / Cosmetico Dental).'
                      : 'Volunteer baseline dental status. It will be auto-loaded when assigned to a dental study.'}
                  </p>
                </div>
                {Object.keys(healthData.dental_status || {}).length > 0 && (
                  <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded" data-testid="qsalud-odonto-count">
                    {Object.values(healthData.dental_status || {}).filter(v => v !== 'normal').length} {lang === 'es' ? 'diente(s) con hallazgos' : 'tooth findings'}
                  </span>
                )}
              </div>
              <p className="text-xs text-stone-500 mb-3">
                {lang === 'es'
                  ? 'Click en un diente para ciclar: Normal → Ausente → Protesis → Implante Unitario → Implante Multiple → Corona → Carilla.'
                  : 'Click a tooth to cycle: Normal → Missing → Prosthesis → Single Implant → Multi Implant → Crown → Veneer.'}
              </p>
              <div className="flex justify-center" data-testid="qsalud-odontograma">
                <DentalChart
                  notationSystem="FDI"
                  teethIncluded={FDI_ALL_TEETH}
                  teethStatus={healthData.dental_status || {}}
                  mode="status"
                  onChangeStatus={(tooth, newStatus) => {
                    setHealthData(prev => ({
                      ...prev,
                      dental_status: { ...(prev.dental_status || {}), [tooth]: newStatus },
                    }));
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-6">
            <Button data-testid="save-volunteer-btn-health" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
              <FloppyDisk size={16} />
              {saving ? '...' : t('save')}
            </Button>
          </div>
        </TabsContent>}

        <TabsContent value="documents" className="mt-6">
          <div className="space-y-3">
            {isEdit && (
              <RemoteConsentSender
                volunteerId={id}
                signerEmail={(legalGuardian?.enabled && legalGuardian?.email) || personalData.email || ''}
                signerPhone={(legalGuardian?.enabled && legalGuardian?.telefono) || personalData.telefono || ''}
                pending={vsPending.filter(d => d.channel === 'remote')}
                allSigned={consentTemplates.filter(t => ['lopd', 'imagenes'].includes(t.type)).every(t => documents[t.type]?.signed)}
                lang={lang}
                onChanged={() => loadVsPending()}
              />
            )}
            {isEdit && (
              <VidsignerSyncBanner
                pending={vsPending.filter(d => d.channel !== 'remote')}
                syncing={vsSyncing}
                onSync={() => syncVidsigner(false)}
                onPoll={() => syncVidsigner(true).then(() => loadVsPending())}
                onCancelled={() => loadVsPending()}
                lang={lang}
              />
            )}
            {isEdit && consentTemplates.length > 0 && consentTemplates.map(tpl => (
              <ConsentDocSection
                key={tpl.template_id}
                template={tpl}
                docData={documents[tpl.type]}
                volunteerId={id}
                onSigned={(docKey, docObj) => setDocuments(prev => ({ ...prev, [docKey]: docObj }))}
                onUpload={(docKey, file) => handleConsentUpload(docKey, file)}
                onDelete={(docKey) => setDocuments(prev => {
                  // URS-F308: limpiar la entrada del state local tras borrado en BD.
                  const next = { ...prev };
                  delete next[docKey];
                  return next;
                })}
                isAdmin={isAdmin}
                uploading={uploading}
                lang={lang}
              />
            ))}
            {(!isEdit || consentTemplates.length === 0) && (
              <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
                <FileText size={40} className="mx-auto text-stone-300 mb-3" />
                <p className="text-sm text-stone-500">{lang === 'es' ? 'Guarda el voluntario primero para gestionar consentimientos' : 'Save the volunteer first to manage consents'}</p>
              </div>
            )}
          </div>
          <div className="flex justify-center mt-6">
            <Button data-testid="save-volunteer-btn-documents" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
              <FloppyDisk size={16} />
              {saving ? '...' : t('save')}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="appointments" className="mt-6">
          <AppointmentsPanel volunteerId={isEdit ? id : null} volunteerName={`${personalData.nombre || ''} ${personalData.apellidos || ''}`.trim()} lang={lang} />
          <div className="flex justify-center mt-6">
            <Button data-testid="save-volunteer-btn-appointments" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
              <FloppyDisk size={16} />
              {saving ? '...' : t('save')}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="studies" className="mt-6">
          <VolunteerStudiesPanel
            volunteerId={isEdit ? id : null}
            volunteerName={`${personalData.nombre || ''} ${personalData.apellidos || ''}`.trim()}
            volunteerEmail={personalData.email || ''}
            lang={lang}
          />
          <div className="flex justify-center mt-6">
            <Button data-testid="save-volunteer-btn-studies" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
              <FloppyDisk size={16} />
              {saving ? '...' : t('save')}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Duplicate DNI blocked dialog */}
      <Dialog open={!!dupBlocked} onOpenChange={v => { if (!v) setDupBlocked(null); }}>
        <DialogContent className="max-w-md" data-testid="dup-blocked-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <WarningCircle size={22} weight="fill" />
              {lang === 'es' ? 'DNI duplicado' : 'Duplicate ID'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-700">
            {lang === 'es'
              ? `Ya existe un voluntario con este DNI: ${dupBlocked}. No se puede crear otro registro con el mismo DNI.`
              : `A volunteer with this ID already exists: ${dupBlocked}. Cannot create another record with the same ID.`}
          </p>
          <DialogFooter>
            <Button onClick={() => setDupBlocked(null)} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm">
              {lang === 'es' ? 'Entendido' : 'Understood'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate email/phone warning dialog */}
      <Dialog open={!!dupWarning} onOpenChange={v => { if (!v) setDupWarning(null); }}>
        <DialogContent className="max-w-md" data-testid="dup-warning-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <WarningCircle size={22} weight="fill" />
              {lang === 'es' ? 'Posible duplicado' : 'Possible duplicate'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {(dupWarning || []).map((w, i) => (
              <p key={i} className="text-sm text-stone-700">{w}</p>
            ))}
            <p className="text-sm text-stone-500 italic">
              {lang === 'es' ? '¿Desea continuar de todos modos?' : 'Do you want to continue anyway?'}
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDupWarning(null)} className="rounded-sm">
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={() => { setDupWarning(null); doSave(); }} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm">
              {lang === 'es' ? 'Guardar de todos modos' : 'Save anyway'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VolunteerAuditTimeline
        volunteerId={id}
        open={showVolAudit}
        onClose={() => setShowVolAudit(false)}
        lang={lang}
      />
    </div>
  );
}
